// Combo content generator — builds unique long-form content per (service × location).
// Strategy: rotate token slots (intent verb, opener, local-context paragraph, use cases,
// delivery line, micro-CTA, faq variants) seeded by (serviceId, locationSlug) so each
// of the ~762 combo pages reads differently while staying truthful and on-brand.

import type { ServiceMeta, ServiceId } from "./services.ts";
import type { Location } from "./locations.ts";
import { getDistrictContext, type DistrictContext } from "./district-context.ts";

// ------- deterministic seed → slot rotator -------
function hash(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}
function pick<T>(arr: T[], seed: number, offset = 0): T {
  return arr[(seed + offset) % arr.length];
}

// ------- service-specific verb / framing pool -------
const SERVICE_VERBS: Record<ServiceId, string[]> = {
  translation: [
    "แปลและรับรองเอกสาร",
    "บริการแปลทุกภาษา",
    "Certified Translation",
    "บริการแปลและรับรองคำแปล",
  ],
  "notary-public": [
    "รับรองเอกสารโดยทนาย Notary Public",
    "บริการ Notary Public",
    "Notarial Services Attorney",
    "ทนายรับรองลายมือชื่อ",
  ],
  naati: [
    "บริการแปล NAATI Certified",
    "แปลเอกสาร NAATI สำหรับวีซ่าออสเตรเลีย",
    "NAATI Translation",
    "นักแปล NAATI รับรอง",
  ],
  "police-clearance": [
    "บริการขอใบตรวจประวัติอาชญากรรม",
    "Police Clearance Certificate",
    "บริการขอ Police Clearance พร้อมรับรอง",
    "ตรวจประวัติอาชญากรสำหรับยื่นวีซ่า",
  ],
  "mfa-legalization": [
    "รับรองเอกสารกงสุล MFA / นิติกรณ์ไทย",
    "บริการรับรองกงสุลกระทรวงการต่างประเทศ",
    "MFA Legalization Service",
    "นิติกรณ์กรมการกงสุล",
  ],
  "embassy-legalization": [
    "รับรองเอกสารโดยสถานทูตทุกประเทศ",
    "Embassy Legalization Service",
    "บริการเดินเอกสารสถานทูต",
    "รับรองเอกสารระดับ Embassy ครบวงจร",
  ],
  "visa-thai-citizens": [
    "บริการยื่นวีซ่าสำหรับคนไทย",
    "ที่ปรึกษาวีซ่าทั่วโลก",
    "Visa Application Service",
    "บริการขอวีซ่า Schengen / UK / US / Australia",
  ],
  "visa-foreigners-thailand": [
    "Thailand Visa & Work Permit Service",
    "Bangkok Immigration Lawyer for Foreigners",
    "Non-B / Non-O / LTR / DTV / Elite Visa Service",
    "Thai Work Permit & Visa Extension Consultant",
  ],
  "marriage-thai-foreign": [
    "บริการจดทะเบียนสมรสไทย–ต่างชาติ",
    "Thai-Foreign Marriage Registration",
    "บริการจดทะเบียนสมรสกับชาวต่างชาติครบวงจร",
    "Affidavit สถานทูต + จดทะเบียนสมรสที่อำเภอ",
  ],
  interpreter: [
    "บริการล่ามแปลภาษามืออาชีพ",
    "ล่ามศาล / ล่ามจดทะเบียนสมรส / ล่ามตม.",
    "Court & Business Interpreter",
    "ล่ามประชุมธุรกิจและล่าม Simultaneous",
  ],
  "apec-abtc": [
    "บริการขอบัตร APEC Business Travel Card",
    "ABTC Application Service",
    "ขอบัตร APEC สำหรับนักธุรกิจไทย",
    "APEC Card ครบวงจร — เตรียมเอกสาร + ยื่นกรมการกงสุล",
  ],
  apostille: [
    "บริการรับรอง Apostille ประเทศไทย",
    "Apostille Service Thailand",
    "Hague Apostille รับรองเอกสารไทย",
    "Apostille MFA สำหรับใช้ใน 130 ประเทศสมาชิก",
  ],
  "tm30-90day": [
    "บริการแจ้ง TM30 และรายงานตัว 90 วัน",
    "TM30 + 90-Day Report Service",
    "ยื่น TM30 / TM47 แทนชาวต่างชาติและเจ้าบ้าน",
    "Immigration Reporting ครบวงจร",
  ],
  "work-permit-boi": [
    "บริการขอใบอนุญาตทำงาน Work Permit",
    "Work Permit + BOI Single Window",
    "ขอ Non-B Visa + Work Permit ครบจบ",
    "Digital Work Permit & Corporate Package",
  ],
  "permanent-residence": [
    "บริการขอใบสำคัญถิ่นที่อยู่ (PR) ประเทศไทย",
    "Thai Permanent Residence Service",
    "ขอ PR ไทย Quota รายปี ทั้ง 5 ประเภท",
    "ที่ปรึกษากฎหมาย ตม. + เตรียมสัมภาษณ์ภาษาไทย",
  ],
  "thai-citizenship": [
    "บริการขอสัญชาติไทย (Thai Citizenship / Naturalization)",
    "Thai Naturalization Service ครบทุกประเภท",
    "ขอสัญชาติไทย — PR 5 ปี / คู่สมรสคนไทย / บุตร / ฟื้นฟูสัญชาติ",
    "ที่ปรึกษากฎหมาย + ติวภาษาไทย+เพลงชาติ + พาสปอร์ตไทย",
  ],
  "company-boi": [
    "บริการจดทะเบียนบริษัท + BOI Promotion ครบวงจร",
    "Company Registration + BOI Investment Promotion",
    "จดบริษัท DBD + ขอ FBL · Treaty of Amity · LTR Visa 10 ปี",
    "Corporate Package + BOI Single Window + Bookkeeping",
  ],
  "trademark-ip": [
    "บริการจดเครื่องหมายการค้า + ทรัพย์สินทางปัญญาครบวงจร",
    "Trademark Registration + Madrid Protocol 130+ ประเทศ",
    "จดสิทธิบัตร · ลิขสิทธิ์ · Trade Secret + ฟ้องคดี CIPITC",
    "ตัวแทนเครื่องหมายการค้าขึ้นทะเบียน DIP + Trademark Watch",
  ],
  "sworn-translation": [
    "บริการแปลรับรองโดยทนาย + ล่ามศาลครบวงจร (Sworn Translation)",
    "Sworn Translation & Court Interpreter — 30+ ภาษา",
    "ทนายสภาฯ ลงนาม + ล่ามขึ้นทะเบียนศาลยุติธรรม",
    "รองรับศาล/DSI/ปคบ./อัยการ/THAC/SIAC พร้อมล่ามฉุกเฉิน 24 ชม.",
  ],
  "criminal-defense": [
    "ทนายอาญาครบวงจร + ประกันตัว 24 ชม. (Criminal Defense & Bail)",
    "Criminal Defense Lawyer — ยาเสพติด ฉ้อโกง ฟอกเงิน ตม. ปคบ. DSI",
    "Hotline 24 ชม. ทนายถึงสถานีใน 2 ชั่วโมง + Bail Bond บริษัท",
    "ทีมทนายอาญา 18+ คน + อดีตอัยการ/พงส. + ล่ามขึ้นทะเบียนศาล 30+ ภาษา",
  ],
  "civil-family-probate": [
    "ทนายแพ่ง · ครอบครัว · หย่า · มรดก ครบวงจร (Civil/Family/Probate)",
    "Divorce · Child Custody · Prenup · Probate · Cross-border Family",
    "หย่ายินยอม + ฟ้องหย่า + สิทธิเลี้ยงดูบุตร + ฟ้องแบ่งมรดก",
    "Hague Convention · International Divorce · Estate Planning · Family Office",
  ],
  "real-estate-conveyancing": [
    "ทนายอสังหาฯ · โอนกรรมสิทธิ์ · Real Estate Conveyancing ครบวงจร",
    "Title Search · Due Diligence · SPA · Same-Day Land Office Transfer",
    "Foreign Condo Quota 49% · Usufruct · Lease 30 ปี · Thai Limited 51:49",
    "Property Tax Planning · BOI Land · Real Estate Litigation · 461 สำนักงานที่ดิน",
  ],
  "tax-law": [
    "ทนายภาษีอากร + อดีตสรรพากร — PIT · CIT · VAT · Transfer Pricing",
    "BEPS 2.0 Pillar Two · Global Minimum Tax 15% · QDMTT · IIR · CbCR",
    "Tax Audit Defense + ฟ้องศาลภาษีอากรกลาง · Win Rate 76%",
    "DTA 61 ประเทศ · International Tax Structuring · LTR Visa Tax Planning",
  ],
  "labor-employment": [
    "ทนายแรงงานเฉพาะทาง — เลิกจ้าง · ค่าชดเชย · ศาลแรงงาน",
    "Mass Layoff · Restructuring · Union Negotiation · CBA",
    "Workplace Investigation · Sexual Harassment · #MeToo",
    "Win Rate ศาลแรงงาน 81% · Settlement 72% · 5,200+ คดี",
  ],
  "banking-finance": [
    "ทนายธนาคาร-การเงินครบวงจร — NPL · Restructuring · IPO · SEC",
    "ฟ้องลูกหนี้ NPL · ปรับโครงสร้างหนี้ · ฟื้นฟูกิจการ Win Rate 92%",
    "IPO SET/mai · หุ้นกู้ · Fintech · Crypto License · AML/CFT",
    "Project Finance ₿185,000M · Rehabilitation ฿385,000M",
  ],
  "pdpa-cybersecurity": [
    "ที่ปรึกษา PDPA · DPO Outsourced · DPIA · Data Breach Response 72 ชม.",
    "GDPR Cross-border · EU SCC 2021 · UK IDTA · BCR · TIA",
    "ISO 27001 · ISO 27701 · NIST CSF · พ.ร.บ.ไซเบอร์ 2562 · CII",
    "Ransomware Response 24/7 · Fine Reduction 78% · 67 Breach Incidents",
  ],
  "international-arbitration": [
    "ทนายอนุญาโตฯ ระหว่างประเทศ — TAI · THAC · SIAC · HKIAC · ICC · LCIA",
    "Investor-State ICSID · UNCITRAL · BIT · MIT · FTA · 168+ คดี",
    "บังคับตามคำชี้ขาด New York Convention 1958 — 172 ประเทศ · Win Rate 89%",
    "Emergency Arbitrator 15 วัน · Asset Freeze · Anti-suit · FCIArb 8 ท่าน",
    "Construction FIDIC · DAB · พิพาทก่อสร้าง EPC/Turnkey",
    "Energy & Resources — PSC · PPA · LNG · Net-zero Transition Dispute",
    "Maritime LMAA · SCMA · Charterparty · Bill of Lading Dispute",
    "Sport Arbitration CAS / TAS · FIFA · FIBA · Doping Tribunal",
    "Multi-tier Dispute Resolution Clause Drafting · Stepped Mediation-Arb",
  ],
  "ltr-elite-visa": [
    "LTR Visa 10 ปี · Thailand Privilege (Elite) · Smart Visa · DTV 5 ปี",
    "Work Permit · PR ใบสำคัญถิ่นที่อยู่ · Thai Citizenship Naturalization",
    "Tax 17% Flat Rate (LTR HS) · Foreign Source Exemption · DTA 61 ประเทศ",
    "Authorized Agent BOI + Thailand Privilege · Approval 96% · 1,850+ เคส",
    "Family Office · Multi-Generation Wealth Migration · Private Trust",
    "Retirement Non-O-A · O-X · Elite Easy Access · Health Insurance USD 50K",
    "Digital Nomad DTV — Workcation · Muay Thai · Cooking · Medical Wellness",
    "Property Acquisition LTR Wealthy — Condo Quota · Lease 30 ปี · Usufruct",
    "Children Education Visa Non-ED · International School · ASEAN Scholarship",
  ],
  "ma-private-equity": [
    "ที่ปรึกษา M&A · PE · VC — Term Sheet · DD · SPA · SHA · ESOP · Exit",
    "Cross-border 18 ประเทศ · 215+ ดีล · ฿285,000M+ · Big Law Caliber",
    "Legal Due Diligence 15 Workstreams · Red Flag · Heat Map · VDR Premier",
    "NVCA VC Model · LBO · Carve-out · PE Fund Formation · IPO Pre-restructuring",
    "Distressed M&A · Pre-pack · Stalking Horse Bidder · Section 90/91",
    "Healthcare Roll-up · Clinic Network · Telemedicine Platform M&A",
    "F&B Chain Consolidation · Franchise M&A · QSR Holding Structure",
    "Real Estate PE — REIT Conversion · Hospitality JV · Logistics Warehouse",
    "Tech M&A — SaaS ARR Multiple · Earnout Milestone · IP Carve-out · Talent Lock-up",
  ],
  "work-experience-certificate": [
    "ขอใบรับรองประสบการณ์ทำงานจากประกันสังคม (สปส.6-09)",
    "ขอใบรับรองจากกระทรวงแรงงาน · กรมการจัดหางาน",
    "บริการแปล + รับรองกงสุล MFA + สถานทูต",
    "Work Experience Certificate Translation",
    "ใบรับรองนายจ้าง · Employment Verification Letter",
  ],
  "medical-council-certification": [
    "รับรองเอกสารโดยแพทยสภา · ทันตแพทยสภา · สภาเภสัชกรรม",
    "Good Standing Certificate ใบประกอบวิชาชีพแพทย์",
    "Medical License Verification Thailand",
    "บริการแปล + Notary + กงสุล สำหรับใบประกอบวิชาชีพ",
    "ใบอนุญาตประกอบวิชาชีพเวชกรรม / ทันตกรรม / เภสัชกรรม / พยาบาล",
  ],
  "notary-public-foreigners": [
    "English-speaking Notary Public for expats",
    "รับรองหนังสือมอบอำนาจ (POA) ภาษาอังกฤษสำหรับชาวต่างชาติ",
    "Affidavit · Statutory Declaration · Signature Witnessing",
    "Certified true copies + MFA + embassy legalisation",
    "Mobile notary service: hotel, office, hospital, jail",
  ],
  "mfa-legalization-foreigners": [
    "Thai MFA consular legalisation in English for expats",
    "Birth / marriage / death / academic / company document legalisation",
    "End-to-end pickup, translation, MFA, embassy, DHL worldwide",
    "บริการ MFA + สถานทูต ภาษาอังกฤษเต็มรูปแบบ",
    "Apostille-ready document preparation",
  ],
  "marriage-abroad-thai": [
    "เตรียมเอกสารคนไทยจดทะเบียนสมรสในต่างประเทศ 40+ ประเทศ",
    "ใบรับรองโสด (Single Status) + แปล + Notary + MFA + สถานทูต",
    "Affirmation of Marital Status สำหรับ USA / UK",
    "Ehefähigkeitszeugnis support สำหรับเยอรมัน",
    "Nulla Osta สำหรับอิตาลี",
  ],
  "naati-online-worldwide": [
    "NAATI Certified Translation Thai ↔ English worldwide",
    "NAATI Digital Stamp 24–48h delivery",
    "Skilled Migration · Partner · Student · AHPRA document set",
    "Acceptance by Australian Home Affairs + Immigration NZ",
    "Bundle pricing for Partner Visa / PR document sets",
  ],
  "government-document-translation": [
    "แปลเอกสารราชการไทยทุกประเภทโดยนักแปลขึ้นทะเบียน MFA",
    "ทะเบียนบ้าน · บัตร ปชช. · สูติบัตร · มรณบัตร · ทะเบียนสมรส/หย่า",
    "โฉนดที่ดิน · สัญญาเช่า · หนังสือรับรองบริษัท · บอจ.5",
    "คำพิพากษา · คำสั่งศาล · ใบแต่งทนาย",
    "รับรองคำแปลพร้อมใช้ยื่นทันที 50+ ภาษา",
  ],
  "government-document-certification": [
    "ขอและรับรองสำเนาเอกสารราชการไทยทุกหน่วยงาน",
    "สูติบัตร · ทะเบียนสมรส · ใบเปลี่ยนชื่อ · ใบรับรองโสด",
    "โฉนดที่ดิน + สารบบ จากกรมที่ดิน",
    "หนังสือรับรองบริษัท + บอจ.5 จาก DBD",
    "สำเนาคำพิพากษาและคำสั่งศาล",
  ],
  "embassy-legalization-by-country": [
    "บริการรับรองสถานทูตในกรุงเทพฯ 30+ ประเทศ",
    "USA · UK · ออสเตรเลีย · นิวซีแลนด์ · แคนาดา · เยอรมัน · ฝรั่งเศส · อิตาลี",
    "ญี่ปุ่น · เกาหลี · จีน · ไต้หวัน · ฮ่องกง · สิงคโปร์ · มาเลเซีย",
    "UAE · ซาอุดิอาระเบีย · กาตาร์ · คูเวต (รวม Chamber of Commerce)",
    "Embassy appointment booking + same-week submission",
  ],
};

// ------- area-class detection (drives unique paragraphs) -------
type AreaClass = "cbd" | "expat" | "tourist" | "residential" | "industrial" | "border" | "branch" | "generic";
function classifyLocation(loc: Location, ctx: DistrictContext): AreaClass {
  const econ = (ctx.economy ?? "").toLowerCase();
  if (loc.type === "headquarters" || loc.type === "branch") return "branch";
  if (econ.includes("cbd") || econ.includes("ธนาคาร") || econ.includes("สำนักงาน")) return "cbd";
  if (econ.includes("ชาวต่างชาติ") || econ.includes("expat") || econ.includes("ไลฟ์สไตล์")) return "expat";
  if (econ.includes("ท่องเที่ยว") || econ.includes("ช้อปปิ้ง")) return "tourist";
  if (econ.includes("นิคม") || econ.includes("eec") || econ.includes("ท่าเรือ")) return "industrial";
  if (econ.includes("ชายแดน")) return "border";
  if (econ.includes("ที่พักอาศัย") || econ.includes("อาศัย")) return "residential";
  return "generic";
}

// ------- local-context opener (5–7 variants per class) -------
const LOCAL_OPENERS: Record<AreaClass, string[]> = {
  cbd: [
    "{name} เป็นย่านธุรกิจที่มีบริษัทข้ามชาติและสำนักงานกฎหมายหนาแน่นที่สุดแห่งหนึ่งของกรุงเทพฯ ลูกค้าส่วนใหญ่ต้องการ {verb} เพื่อใช้ในธุรกรรมระหว่างประเทศ — เซ็นสัญญา จดทะเบียนสาขา ยื่นเสนองานต่างประเทศ — โดยมีเงื่อนไขสำคัญคือ “ต้องเสร็จเร็วและถูกต้องตามมาตรฐานกระทรวงการต่างประเทศ”",
    "พื้นที่ {name} เป็นศูนย์กลางทางการเงินและสำนักงานใหญ่ของหลายองค์กรระดับโลก งาน {verb} จึงมักเร่งด่วนและต้องการความแม่นยำสูงสุด เราเข้าใจขั้นตอนของฝ่ายกฎหมายและฝ่ายบุคคลของบริษัทเหล่านี้ทุกขั้นตอน",
    "ใน {name} เราพบลูกค้ากลุ่มบริหารและทีมกฎหมายขององค์กรเข้ามาขอ {verb} แทบทุกสัปดาห์ — เอกสารหลักได้แก่ Power of Attorney, Board Resolution, สัญญา MOU และ Affidavit ที่ต้องส่งไปต่างประเทศภายในเวลาที่กำหนด",
  ],
  expat: [
    "{name} เป็นย่านที่พักของชาวต่างชาติและครอบครัวคู่สมรสไทย-เทศจำนวนมาก ลูกค้ามักต้องการ {verb} เพื่อยื่นวีซ่าคู่สมรส ขออนุญาตทำงาน หรือจดทะเบียนสมรส — ทุกขั้นตอนเราอธิบายเป็นภาษาอังกฤษ/จีน/ญี่ปุ่นได้ตามต้องการ",
    "ลูกค้าใน {name} จำนวนมากเป็นคู่รักไทย-ต่างชาติที่กำลังเตรียมเอกสารแต่งงานหรือต่อวีซ่า งาน {verb} ที่เราทำให้บ่อยที่สุดได้แก่ Affidavit of Marital Status, ใบรับรองโสด, ทะเบียนสมรส และ Statement การเงิน",
    "{name} เต็มไปด้วยคอนโดของ expat และบริษัทข้ามชาติ การ {verb} จึงต้องตอบโจทย์ทั้งภาษาอังกฤษและภาษาที่สาม เรามีนักแปลเจ้าของภาษากว่า 50 ภาษา + ทนาย Notary Public 6 ท่านประจำสำนักงาน",
  ],
  tourist: [
    "พื้นที่ {name} มีนักท่องเที่ยวและชาว expat ที่อาศัยระยะยาวจำนวนมาก ลูกค้ามักขอ {verb} เพื่อต่อวีซ่า Retirement, จดทะเบียนสมรส, ซื้อขายอสังหาริมทรัพย์ หรือเตรียมเอกสารกลับประเทศ",
    "{name} เป็นจุดหมายของนักท่องเที่ยวและผู้พำนักระยะยาวจากทั่วโลก งาน {verb} ที่นี่ต้องการความเข้าใจหลายภาษาและรู้ขั้นตอนของแต่ละสถานทูต — เรามีประสบการณ์งานลูกค้าจาก 40+ สัญชาติ",
    "ลูกค้าใน {name} ที่ใช้บริการ {verb} ของเรามักเป็น expat ที่ต้องการเอกสารเดินทาง, เปิดบัญชีธนาคาร, หรือยื่นขอ Long-stay Visa — เราออกใบเสร็จ/ใบกำกับภาษีในนามนิติบุคคลและบุคคลได้",
  ],
  residential: [
    "{name} เป็นย่านที่อยู่อาศัยขนาดใหญ่ที่ลูกค้าส่วนใหญ่เป็นครอบครัวคนไทยและคนทำงาน บริการ {verb} ที่เราให้บ่อยคือเอกสารสมัครงานต่างประเทศ, เรียนต่อ, จดทะเบียนสมรสกับคู่ครองชาวต่างชาติ และเอกสาร Police Clearance",
    "พื้นที่ {name} มีประชากรหนาแน่นและครอบคลุมหลายสายอาชีพ ลูกค้ามักขอ {verb} ในเอกสารสำคัญในครอบครัว — สูติบัตร, ทะเบียนบ้าน, ใบเปลี่ยนชื่อ, ใบมรณบัตร — เราเก็บ-ส่งถึงบ้านได้ทุกวัน",
    "ใน {name} เราให้บริการ {verb} ที่บ้านลูกค้าได้โดยตรง ทีมงานเดินทางรับเอกสารและส่งคืนเอกสารฉบับรับรอง ลูกค้าไม่ต้องเสียเวลาเดินทางมาที่สำนักงาน",
  ],
  industrial: [
    "{name} เป็นพื้นที่นิคมอุตสาหกรรมและพอร์ตการขนส่งสำคัญ ลูกค้ากลุ่มโรงงานและบริษัท Logistics มักต้องการ {verb} สำหรับเอกสารส่งออก, สัญญา OEM และเอกสาร Work Permit ของชาวต่างชาติที่เข้ามาทำงาน",
    "พื้นที่ {name} ครอบคลุมโรงงานและบริษัทขนส่งระหว่างประเทศ บริการ {verb} ที่เราดำเนินการบ่อยคือ Certificate of Origin, Commercial Invoice แปลรับรอง และ Visa Documents ของวิศวกร/ผู้บริหารต่างชาติ",
    "{name} เป็นศูนย์กลาง EEC/นิคมอุตสาหกรรม ทำให้งาน {verb} มีปริมาณสูงและเร่งด่วน เรามีทีม Account Manager รับเรื่องบริษัทใหญ่โดยเฉพาะ พร้อม SLA เร่งด่วน 4 ชั่วโมง",
  ],
  border: [
    "{name} เป็นจุดผ่านแดนสำคัญที่ลูกค้าต้องการ {verb} สำหรับเอกสารข้ามแดน, ใบรับรองธุรกิจชายแดน, และเอกสารสำหรับลูกค้าฝั่งสปป.ลาว — ทีมงานในพื้นที่ของเราดำเนินการได้ในวันเดียว",
    "พื้นที่ {name} ใกล้สะพานมิตรภาพและจุดผ่านแดน ลูกค้าจำนวนมากต้องการ {verb} สำหรับธุรกรรมไทย-ลาว — เราคุ้นเคยทั้งภาษาลาวและกระบวนการ MFA ของทั้งสองประเทศ",
  ],
  branch: [
    "{name} เป็นสำนักงานของเราที่ให้บริการ {verb} แบบครบวงจร — ลูกค้าเดินทางเข้ามาที่สาขาเพื่อยื่นเอกสารต้นฉบับและรับเอกสารฉบับรับรองกลับได้ภายในวันเดียว (สำหรับงานเอกสารไม่ซับซ้อน)",
    "ที่ {name} ทีมงานพร้อมให้บริการ {verb} ทุกวันทำการ มีห้องประชุมส่วนตัวสำหรับลูกค้านิติบุคคลและห้องเซ็นเอกสารต่อหน้าทนาย Notary Public",
    "สำนักงาน {name} ของเราเป็นจุดให้บริการ {verb} หลักของภูมิภาค — รองรับเอกสารทั้งภาษาไทย, อังกฤษ และภาษาท้องถิ่นของลูกค้าในพื้นที่",
  ],
  generic: [
    "ใน {name} เราให้บริการ {verb} ครบวงจรสำหรับลูกค้าทั้งบุคคลและองค์กร ตั้งแต่ขั้นตอนประเมินเอกสาร แปล รับรอง ไปจนถึงนำส่งกระทรวงและสถานทูต ลูกค้าไม่ต้องเดินทางหลายที่อีกต่อไป",
    "บริการ {verb} ในพื้นที่ {name} เปิดให้ใช้งานทั้ง walk-in ที่สาขาของเรา และแบบออนไลน์ — ส่งเอกสารทาง LINE/อีเมล รับเอกสารคืนทาง EMS ทั่วประเทศ ฿100",
    "ลูกค้าใน {name} เลือกใช้ {verb} ของเราด้วยเหตุผล 3 ข้อ: ดำเนินการเร็ว, ทีมตอบใน 15 นาที, และเอกสารทุกชิ้นได้รับการยอมรับจาก MFA และสถานทูตทั่วโลก",
  ],
};

// ------- delivery / pickup paragraph -------
function deliveryParagraph(loc: Location, ctx: DistrictContext, seed: number): string {
  const transitLine =
    ctx.transit.length > 0
      ? `ลูกค้าที่เดินทางด้วยรถไฟฟ้าสามารถใช้สถานี ${ctx.transit.slice(0, 3).join(" · ")} เพื่อเข้าถึงพื้นที่ ${loc.nameTh} ได้อย่างสะดวก`
      : `เราเข้าถึง ${loc.nameTh} ผ่านถนนสายหลักและทางพิเศษ ทำให้รถรับ-ส่งเอกสารของเราถึงลูกค้าได้ภายในวันเดียวกัน`;
  const mallLine =
    ctx.malls.length > 0
      ? `จุดนัดรับเอกสารยอดนิยมคือ ${ctx.malls.slice(0, 2).join(" · ")} ซึ่งใกล้กับพื้นที่ลูกค้าและมีที่จอดรถสะดวก`
      : `เรามีจุดนัดรับเอกสารยืดหยุ่นตามที่ลูกค้าสะดวก ตั้งแต่ที่ทำงาน คอนโด ไปจนถึงร้านกาแฟ`;
  const closing = pick(
    [
      `ทีม Logistics ของเรารับ-ส่งเอกสารถึงมือลูกค้าใน ${loc.nameTh} ภายใน 1 วันทำการ`,
      `เรารับ-ส่งเอกสารผ่าน EMS/Kerry/Lalamove ตามความเร่งด่วน ค่าจัดส่งเริ่ม ฿100 ในประเทศและ ฿2,500 ทั่วโลก`,
      `เอกสารทุกชิ้นมีหมายเลข Tracking ลูกค้าใน ${loc.nameTh} ติดตามสถานะได้ตลอด 24 ชั่วโมง`,
    ],
    seed,
    7,
  );
  return `${transitLine} ${mallLine} ${closing}`;
}

// ------- neighbor / cluster paragraph -------
function neighborParagraph(loc: Location, ctx: DistrictContext): string {
  if (ctx.neighbors.length === 0) {
    return `นอกจากพื้นที่ ${loc.nameTh} แล้ว เรายังให้บริการครอบคลุมพื้นที่ใกล้เคียงและเขตเศรษฐกิจสำคัญในภูมิภาคเดียวกัน ลูกค้าสามารถเลือกจุดรับ-ส่งเอกสารที่สะดวกที่สุดได้`;
  }
  return `บริการของเราใน ${loc.nameTh} ครอบคลุมพื้นที่ใกล้เคียง ได้แก่ ${ctx.neighbors.join(" · ")} — ลูกค้าจากพื้นที่ข้างเคียงสามารถใช้บริการ pickup เดียวกันได้ เพื่อประหยัดเวลาและค่าจัดส่ง`;
}

// ------- LSI / use-case bullets per service -------
const SERVICE_USE_CASES: Record<ServiceId, string[]> = {
  translation: [
    "แปลเอกสารยื่นวีซ่า Schengen / UK / US / Australia / Canada",
    "แปล Transcript และปริญญาบัตรสำหรับเรียนต่อต่างประเทศ",
    "แปลทะเบียนสมรส/ใบหย่าสำหรับใช้ในต่างประเทศ",
    "แปลสัญญาธุรกิจ MOU NDA สำหรับนิติบุคคล",
    "แปล Statement บัญชีและงบการเงินสำหรับยื่นวีซ่า",
    "แปลเอกสารแพทย์/กฎหมาย/วิศวกรรมเฉพาะทาง",
  ],
  "notary-public": [
    "รับรองลายมือชื่อใน Power of Attorney",
    "รับรองสำเนาเอกสารสำหรับใช้ในต่างประเทศ",
    "รับรอง Affidavit / Statutory Declaration",
    "รับรองหนังสือยินยอมและพินัยกรรม",
    "รับรอง Board Resolution สำหรับบริษัทข้ามชาติ",
    "รับรองเอกสารสำหรับการค้า Import/Export",
  ],
  naati: [
    "NAATI สำหรับยื่นวีซ่านักเรียนออสเตรเลีย (Student Visa)",
    "NAATI สำหรับวีซ่าคู่สมรส (Partner Visa)",
    "NAATI สำหรับวีซ่าทำงาน (Skilled Visa)",
    "NAATI สำหรับ Permanent Residency",
    "NAATI สำหรับ Citizenship Application",
    "NAATI สำหรับใช้ในนิวซีแลนด์และประเทศ Commonwealth",
  ],
  "police-clearance": [
    "Police Clearance สำหรับยื่นวีซ่า Skilled/Work Visa",
    "Police Clearance สำหรับ Permanent Residency",
    "Police Clearance สำหรับสมัครงานต่างประเทศ",
    "Police Clearance สำหรับ Citizenship Application",
    "ตรวจประวัติพนักงานสำหรับองค์กร",
    "ตรวจประวัติตัวเองสำหรับใช้ภายในประเทศ",
  ],
  "mfa-legalization": [
    "นิติกรณ์เอกสารราชการไทยสำหรับใช้ในต่างประเทศ",
    "รับรองเอกสารการเรียน/วุฒิการศึกษา",
    "รับรองเอกสารทะเบียนราษฎร์ (สูติบัตร/มรณบัตร/ทะเบียนสมรส)",
    "รับรองเอกสารบริษัท/หนังสือรับรอง DBD",
    "รับรองเอกสารสำหรับยื่นต่อสถานทูต",
    "Apostille-equivalent สำหรับประเทศที่ไม่ใช่สมาชิก",
  ],
  "embassy-legalization": [
    "รับรองสถานทูตจีน ญี่ปุ่น เกาหลี",
    "รับรองสถานทูตสหรัฐฯ / แคนาดา / อังกฤษ / ออสเตรเลีย",
    "รับรองสถานทูต Schengen (เยอรมัน / ฝรั่งเศส / อิตาลี / สเปน / ดัตช์)",
    "รับรองสถานทูตตะวันออกกลาง (UAE / Saudi / Qatar)",
    "รับรองสถานทูตอินเดีย ปากีสถาน เวียดนาม",
    "รับรองสถานทูตเพื่อยื่นวีซ่าและจดทะเบียนสมรส",
  ],
  "visa-thai-citizens": [
    "ยื่นวีซ่า Schengen Tourist · Business · Family",
    "ยื่นวีซ่า UK Standard Visitor · Student · Skilled Worker",
    "ยื่นวีซ่า US B1/B2 · F1 · J1 · H1B",
    "ยื่นวีซ่า Australia 600 · 500 · 482 · Partner",
    "ยื่นวีซ่า Canada Visitor · Study · Work",
    "ยื่นวีซ่า Japan · Korea · China · นิวซีแลนด์",
    "Refusal Recovery — ยื่นใหม่หลังถูกปฏิเสธ",
    "โค้ชสัมภาษณ์ Mock Interview สถานทูต",
  ],
  "visa-foreigners-thailand": [
    "Non-B Business Visa + Work Permit for foreign professionals",
    "Non-O Marriage Visa (spouse of Thai citizen)",
    "Non-O / O-A / O-X Retirement Visa (age 50+)",
    "Thailand Privilege (Elite) Visa 5–20 years",
    "DTV Destination Thailand Visa for digital nomads",
    "LTR Long-Term Resident Visa (BOI) 10-year",
    "Change of Visa Status (Tourist → Non-B / Non-O)",
    "Permanent Residency & Thai Citizenship pathway",
    "90-Day Report, Re-Entry Permit, TM30 compliance",
  ],
  "marriage-thai-foreign": [
    "Affidavit of Marital Status ที่สถานทูตของฝ่ายต่างชาติ",
    "แปล + รับรอง MFA Affidavit เป็นภาษาไทย",
    "พาคู่สมรสไปจดที่สำนักงานเขต/อำเภอพร้อมล่าม",
    "แปล + รับรอง MFA + สถานทูตทะเบียนสมรสเพื่อใช้ต่างประเทศ",
    "เตรียมเอกสาร K1 / Spouse Visa US · UK · Australia · Schengen",
    "Non-O Marriage Visa สำหรับฝ่ายต่างชาติในไทย",
    "ร่าง Prenuptial Agreement (สัญญาก่อนสมรส) 2 ภาษา",
    "บันทึก คร.22 ทะเบียนฐานะแห่งครอบครัวกรณีจดที่ต่างประเทศมาก่อน",
    "เปลี่ยนนามสกุล + สิทธิประโยชน์ครอบครัวหลังจดทะเบียน",
  ],
  interpreter: [
    "ล่ามวันจดทะเบียนสมรสกับชาวต่างชาติที่สำนักงานเขต/อำเภอ",
    "ล่ามวันเซ็น Affidavit / Power of Attorney ที่สถานทูต",
    "ล่ามสำหรับสอบสวนตำรวจหรือ ตม.",
    "ล่ามชั้นศาล (แพ่ง อาญา ครอบครัว แรงงาน IP) ขึ้นทะเบียนศาลยุติธรรม",
    "ล่ามไกล่เกลี่ย / Mediation / Arbitration",
    "ล่ามประชุมเจรจาธุรกิจ M&A · OEM · Joint Venture",
    "ล่าม Simultaneous พร้อมตู้ล่าม + หูฟังสำหรับสัมมนา",
    "ล่ามทางการแพทย์ที่โรงพยาบาลเอกชนชั้นนำ",
    "ล่ามทาง Zoom / Teams / Webex สำหรับงานข้ามประเทศ",
  ],
  "apec-abtc": [
    "ขอบัตร ABTC สำหรับ CEO / กรรมการบริษัทที่เดินทาง APEC บ่อย",
    "ขอบัตร ABTC สำหรับผู้บริหารฝ่ายขาย / Sourcing ที่ดูงานจีน เกาหลี ญี่ปุ่น เวียดนาม",
    "ขอบัตร ABTC สำหรับเจ้าของธุรกิจ Export / Import",
    "ต่ออายุบัตร APEC ก่อนหมดอายุ (Renewal 5 ปี)",
    "ขอบัตรใหม่กรณีบัตรหายระหว่างเดินทางต่างประเทศ",
    "แปล + รับรอง MFA เอกสารบริษัทเป็นภาษาอังกฤษสำหรับ pre-clearance",
    "Notary Public รับรองหนังสือมอบอำนาจให้บริษัทเราดำเนินการแทน",
    "ที่ปรึกษากลยุทธ์เพิ่มโอกาส pre-clearance ของจีน รัสเซีย เกาหลี",
    "ขอเพิ่ม Economy ที่ไม่ได้รับอนุมัติในรอบแรก (Top-up Pre-Clearance)",
  ],
  apostille: [
    "Apostille สูติบัตร / ทะเบียนบ้าน / ทะเบียนสมรส สำหรับใช้ในประเทศสมาชิก Hague",
    "Apostille ปริญญาบัตร / Transcript สำหรับเรียนต่อต่างประเทศ",
    "Apostille หนังสือรับรองบริษัท (DBD) สำหรับเปิดสาขาในต่างประเทศ",
    "Apostille Power of Attorney + Notary Public สำหรับขายอสังหาริมทรัพย์ในต่างประเทศ",
    "Apostille Police Clearance Certificate สำหรับยื่นวีซ่า PR / Citizenship",
    "Apostille Affidavit of Marital Status สำหรับจดทะเบียนสมรสในต่างประเทศ",
    "Apostille คำแปลภาษาอังกฤษโดยนักแปลขึ้นทะเบียนกรมการกงสุล",
    "Apostille เอกสารแพทย์ / ใบรับรองแพทย์ สำหรับใช้ในต่างประเทศ",
    "Apostille เอกสารศาล / คำพิพากษา สำหรับใช้ในคดีต่างประเทศ",
  ],
  "tm30-90day": [
    "แจ้ง TM30 ออนไลน์ (e-Notification) ภายใน 24 ชั่วโมงหลังเข้าพัก",
    "รายงานตัว 90 วัน (TM47) ออนไลน์/ที่ ตม. ทั่วประเทศ",
    "Annual Package ฿12,000/ปี — 90-day 4 ครั้ง + TM30 ไม่จำกัด + ระบบเตือน",
    "Hotel/Apartment Package แจ้ง TM30 รายเดือนไม่จำกัดห้อง",
    "Landlord Package สำหรับเจ้าของอพาร์ตเมนต์ 10+ ผู้เช่า",
    "เคลียร์ย้อนหลัง TM30/90-day พร้อมจ่ายค่าปรับแทน",
    "ขอใบ Certificate of Residence สำหรับเปิดบัญชี/ขับรถ/ซื้อรถ",
    "แจ้ง TM30 ใหม่ทุกครั้งที่กลับเข้าประเทศหรือย้ายที่พัก",
    "ระบบเตือนล่วงหน้า LINE/email 14/7/1 วันก่อนครบกำหนด",
  ],
  "work-permit-boi": [
    "ขอ Work Permit ใหม่ผ่าน DOE สำหรับบริษัททั่วไป",
    "ขอ Work Permit ผ่าน BOI Single Window (3 ชั่วโมง) สำหรับบริษัท BOI/IEAT/EEC",
    "ขอ Non-B Visa ใหม่ที่สถานทูตไทยในต่างประเทศ + Invitation Letter + WP3",
    "เปลี่ยนวีซ่า Tourist/Visa Exempt → Non-B ที่ ตม. แจ้งวัฒนะ",
    "ต่ออายุ Work Permit + Non-B Visa ประจำปี (Annual Renewal)",
    "เปลี่ยนนายจ้าง / เปลี่ยนตำแหน่ง / เพิ่มสถานที่ทำงาน",
    "Digital Work Permit (e-Workpermit) — Migrate จากเล่มเดิม",
    "BOI WP10/WP11 Position Approval สำหรับบริษัทบัตรส่งเสริม",
    "Corporate Package + HR Dashboard ติดตามวันหมดอายุ WP/Visa พนักงานทั้งหมด",
  ],
  "permanent-residence": [
    "ประเมิน Point Scoring + เลือกประเภท PR (Investment/Working/Family/Expert/Other)",
    "เตรียมเอกสาร 70–100 รายการ + Personal Statement ภาษาไทย",
    "ติวสัมภาษณ์ภาษาไทย One-on-One 10 ครั้ง + Mock Interview 3 รอบ",
    "ยื่นในรอบ Quota ปลายปี (ต.ค.–ธ.ค.) ที่ ตม. แจ้งวัฒนะ",
    "ติดตามผลกับ ตม. และกระทรวงมหาดไทยทุก 30 วัน",
    "ขอใบสำคัญประจำตัวคนต่างด้าว (เล่มแดง) + ทะเบียนบ้านท.ร.14",
    "Re-Entry Permit แบบ Multiple Entry รายปี",
    "Family Package ครอบครัวขอ PR พร้อมกัน (ประหยัด 30%)",
    "ต่อยอดขอสัญชาติไทยหลังถือ PR ครบ 5 ปี",
  ],
  "thai-citizenship": [
    "ประเมิน Naturalization Score + เลือกช่องทาง (มาตรา 9/10/12)",
    "เตรียมเอกสาร 80–120 รายการ + Personal Statement ภาษาไทย 5–7 หน้า",
    "ติวภาษาไทยขั้นกลาง + เพลงชาติ-สรรเสริญพระบารมี 15 ครั้ง",
    "ยื่นที่กองสัญชาติ กรมการปกครอง (เปิดรับตลอดปี ไม่มี Quota)",
    "Mock Interview กับอดีตตำรวจสันติบาล + คณะกรรมการกลั่นกรอง",
    "ติดตามผลกับ มท. และ ครม. ทุก 60 วัน + รายงานลูกค้า",
    "ขอบัตรประชาชนไทย + พาสปอร์ตไทยหลังประกาศราชกิจจา",
    "ฟื้นฟูสัญชาติไทยเดิม (กรณีหญิงไทยสมรสก่อนปี 2535)",
    "Family Package สามี/ภรรยา/บุตรขอพร้อมกัน (ประหยัด 25%)",
  ],
  "company-boi": [
    "จดทะเบียนบริษัทจำกัด (Ltd.) ที่ DBD ภายใน 5–7 วันทำการ",
    "ขอบัตรส่งเสริมการลงทุน BOI (ยกเว้นภาษี 3–13 ปี · ต่างชาติ 100%)",
    "ขอ Foreign Business License (FBL) จาก MOC สำหรับ List 2/3",
    "Treaty of Amity สำหรับ US Citizens — ถือหุ้น 100% ไม่ต้อง FBL",
    "ขอ LTR Visa 10 ปี — Wealthy/Pensioner/Professional/Highly-Skilled",
    "IEAT/EEC License นิคมอุตสาหกรรม + เขตเศรษฐกิจพิเศษภาคตะวันออก",
    "Branch Office / Representative Office สำหรับบริษัทต่างประเทศ",
    "Corporate Package — DBD + BOI + WP 3 คน + LTR + Bookkeeping",
    "Bookkeeping + Audit + Annual Report รายเดือน/ปี (TFRS)",
  ],
  "trademark-ip": [
    "ตรวจค้นชื่อ/โลโก้ในฐานข้อมูล DIP + WIPO Global Brand Database",
    "จดทะเบียนเครื่องหมายการค้าที่ DIP (Nice Classification 45 หมวด)",
    "Madrid Protocol ยื่นใบเดียวขยายคุ้มครอง 130+ ประเทศ",
    "จดสิทธิบัตรการประดิษฐ์ 20 ปี / อนุสิทธิบัตร 10 ปี / Design Patent",
    "จดทะเบียนลิขสิทธิ์ Source Code · UI/UX · งานสร้างสรรค์ทุกประเภท",
    "Cease & Desist Letter + Customs Recordation ห้ามนำเข้าของปลอม",
    "ฟ้องคดีละเมิด IP ที่ศาล CIPITC + เรียกค่าเสียหาย + ทำลายของกลาง",
    "Trademark Watch Service ติดตามการละเมิดในไทย + ต่างประเทศรายเดือน",
    "Trademark Portfolio Management + ต่ออายุทุก 10 ปี + License/Assignment",
  ],
  "sworn-translation": [
    "แปลรับรองโดยทนายขึ้นทะเบียนสภาทนายความ — ใช้ในศาลทุกประเภท",
    "ล่ามศาลขึ้นทะเบียนศาลยุติธรรม รองรับ 30+ ภาษา",
    "ล่ามสอบสวน DSI · ปคบ. · ตำรวจ · สำนักงานอัยการสูงสุด",
    "ล่ามอนุญาโตตุลาการ THAC · TAI · SIAC · ICC · HKIAC · LCIA",
    "แปลคำพิพากษา + Recognition & Enforcement คำพิพากษาต่างประเทศ",
    "ล่ามฉุกเฉิน 24 ชม. — สถานีตำรวจ · ตม. · โรงพยาบาล · Bail Hearing",
    "ระบบเช็ค Conflict of Interest 3 ชั้น + NDA + AES-256 ทุกเคส",
    "Sworn Interpreter สำหรับ Notary/Affidavit + บริการล่ามสาบานตน",
    "ทีมทนาย 22+ คน + ล่าม Top-tier เครือข่ายสิงคโปร์ ฮ่องกง ลอนดอน",
  ],
  "criminal-defense": [
    "Hotline 24 ชม. — ทนายเวรถึงสถานีตำรวจในกรุงเทพ/ปริมณฑลใน 2 ชั่วโมง",
    "ทนายเข้าร่วมการสอบสวนตามสิทธิ ป.วิ.อ.ม.134/1 + ป้องกันคำให้การถูกบังคับ",
    "ประกันตัวด้วย Bail Bond ของบริษัทเอง 10–15% ของวงเงิน — ไม่ต้องวิ่งหาหลักทรัพย์",
    "คดียาเสพติด — แยก 'เสพ' vs 'จำหน่าย' + เข้าโครงการบำบัดแทนโทษ",
    "คดี Cyber/Crypto — Blockchain Forensic + เครือข่าย Chainalysis",
    "คดี ปคบ./DSI/ปปง. — ทีมอดีตอัยการและพนักงานสอบสวนเข้าร่วมวางแผน",
    "คดี ตม. — Overstay · Blacklist · Humanitarian Petition · กลับเข้าไทย",
    "Prisoner Transfer Treaty — ย้ายนักโทษกลับประเทศ 40+ ประเทศที่ไทยเป็นภาคี",
    "ปกป้องทรัพย์ที่ถูก ปปง. อายัด — Good Faith Third Party Defense",
  ],
  "civil-family-probate": [
    "หย่ายินยอม + ร่างข้อตกลงท้ายทะเบียน — เสร็จ 1 วัน ฿8,500–฿35,000",
    "ฟ้องหย่าตาม ป.พ.พ.ม.1516 (10 เหตุ) + ฟ้องแบ่งสินสมรส",
    "Prenuptial Agreement สำหรับคู่ Thai-Foreign — ร่าง 2 ภาษา + จดทะเบียน",
    "ฟ้องสิทธิปกครอง/พบบุตร + เรียกค่าอุปการะเลี้ยงดู (Sole/Joint Custody)",
    "Hague Child Abduction Convention — Return Order ผ่านศาลเยาวชนฯ",
    "ตั้งผู้จัดการมรดก + แบ่งมรดก + Cross-border Probate 4+ ประเทศ",
    "Estate Planning + พินัยกรรม + ภาษีมรดก + Family Holding Company",
    "ฟ้องผิดสัญญา/ละเมิด/เรียกหนี้ + บังคับคดี 5% ของยอดที่บังคับได้",
    "ครอบครองปรปักษ์ที่ดิน 10 ปี + คดีที่ดิน/อสังหาฯ + รับรองบุตร/บุตรบุญธรรม",
  ],
  "real-estate-conveyancing": [
    "Title Search กรมที่ดิน 30 ปี — ภาระจำนอง · ภาระจำยอม · เช่า · อายัด",
    "Due Diligence ครบชุด 15-30 หน้า — โฉนด · ผังเมือง · EIA · Developer Background",
    "ร่าง SPA 2 ภาษา (ไทย-อังกฤษ) — Subject to Finance · Escrow · Penalty Clause",
    "Same-Day Closing ที่กรมที่ดิน — ทนาย + ล่ามไปด้วยทุกขั้นตอน 3-5 ชม.",
    "Foreign Condo Quota 49% — FET + ใบ ตท.3 + นิติบุคคลรับรอง",
    "Usufruct ตลอดชีพ + Lease 30 ปี + Renewal 30+30 + Superficies",
    "Thai Limited Company 51:49 + Preferred Shares + Voting Control (ไม่ใช่ Nominee)",
    "Property Tax Optimization — ประหยัด 2.8-5%+ ผ่าน Holding Period + Structure",
    "Real Estate Litigation — ฟ้อง Developer · เพิกถอน · บังคับโอน · ภาระจำยอม",
  ],
  "tax-law": [
    "Tax Health Check 3-5 ปีย้อนหลัง + Tax Memo + Action Plan",
    "PIT Optimization สำหรับ Expat + LTR Visa Flat Tax 17%",
    "CIT Compliance ครบปี — ภงด.50/51 · BOI Reporting · Tax Provision",
    "Transfer Pricing Documentation — Local + Master File + CbCR (พ.ร.ก.TP 2562)",
    "BEPS 2.0 Pillar Two GMT 15% + QDMTT + IIR + Safe Harbour",
    "DTA Treaty Shopping Defense + Permanent Establishment Risk Analysis",
    "Tax Audit Defense — ลด Assessment เฉลี่ย 65% · Win Rate 84%",
    "ฟ้องศาลภาษีอากรกลาง + อุทธรณ์ + ฎีกาภาษี · Win Rate 76%",
    "Estate & Gift Tax + Trust Structure + Family Holding · Crypto Tax",
  ],
  "labor-employment": [
    "Employment Contract + Non-Compete + NDA + IP Assignment (ไทย-อังกฤษ)",
    "Employee Handbook + HR Policy ครบชุด · Code of Conduct · DEI",
    "เลิกจ้างถูกกฎหมาย — คำนวณค่าชดเชยตาม พ.ร.บ.คุ้มครองแรงงาน ม.118",
    "Mass Layoff / Restructuring 50+ คน — Union Negotiation + PR Crisis",
    "Workplace Investigation — Sexual Harassment · Fraud · #MeToo · Whistleblower",
    "Labor Court Litigation — สู้ทั้งฝั่งนายจ้างและลูกจ้าง · Win Rate 81%",
    "Union & Collective Bargaining — เจรจาสหภาพ · CBA Drafting · Strike Prevention",
    "Workmen Compensation — อุบัติเหตุจากงาน · ทุพพลภาพ · เสียชีวิต",
    "Expat HR — Work Permit · LTR · Termination · 7-day Repatriation",
  ],
  "banking-finance": [
    "ฟ้อง NPL + บังคับชำระหนี้ + ยึดทรัพย์ + ขายทอดตลาด · Recovery 68%",
    "Debt Restructuring — Out-of-Court · BOT Pre-emptive · CDRAC",
    "Business Rehabilitation (ฟื้นฟูกิจการ) — ศาลล้มละลายกลาง · Planner 38 เคส",
    "IPO + SET/mai Listing — Filing 69-1 · Prospectus · 18 บริษัท ฿128,000M",
    "Securities Issuance — หุ้นกู้ · Perpetual · Sukuk · Sustainability-linked",
    "SEC License — Asset Management · IA · IB · Broker · Custodian",
    "Fintech & Crypto License — Exchange · Broker · ICO Portal · DLT",
    "Insider Trading + Market Manipulation Defense · Win Rate 71%",
    "AML/CFT + UBO + Sanctions Screening Program (280+ บริษัท)",
  ],
  "pdpa-cybersecurity": [
    "PDPA Gap Assessment 87 จุด + Data Mapping + ROPA ครบ ม.39",
    "DPO Outsourced — 95+ องค์กร · ตอบ Subject Request · Liaison สคส.",
    "DPIA สำหรับ Profiling · Biometric · CCTV · HR Analytics · AI",
    "Data Breach Response 4 ชม. → 72 ชม. Notification · สคส.+ICO+CNIL",
    "Cross-border Transfer — EU SCC 2021 · UK IDTA · BCR · TIA · DPF",
    "Cookie Consent Management Platform (OneTrust · Cookiebot · TrustArc)",
    "ISO 27001 + ISO 27701 + NIST CSF Implementation · 65+ Certified",
    "พ.ร.บ.ไซเบอร์ 2562 · CII Registration · NCSA · Incident Reporting",
    "Ransomware Negotiation 24/7 · OFAC Screening · Recovery Rate 87%",
  ],
  "international-arbitration": [
    "Arbitration Clause Drafting + Forum Selection (SIAC/HKIAC/ICC/TAI/THAC)",
    "Request for Arbitration + Statement of Claim + Document Production",
    "Emergency Arbitrator 15 วัน · Asset Freeze · Anti-suit · Security for Costs",
    "Domestic Arbitration TAI/THAC + International SIAC/HKIAC/ICC · 168+ คดี",
    "Investor-State Arbitration ICSID/UNCITRAL (BIT/MIT/FTA) · Damages USD 10M-1B+",
    "Expedited Arbitration (≤ USD 6M) · 6 เดือน · Sole Arbitrator",
    "Enforcement of Foreign Award (NY Convention 1958) · Win Rate 89% · 172 ประเทศ",
    "Set-Aside Application (Art 34 UNCITRAL ML / ม.40 พ.ร.บ.อนุญาโตฯ)",
    "Mediation + Med-Arb + Singapore Convention 2019 · Settlement Rate 73%",
    "Construction Arbitration FIDIC Red/Yellow/Silver · DAB/DAAB · EPC Turnkey",
    "Maritime LMAA · SCMA · Hong Kong MAA · Charterparty + Bill of Lading",
    "Energy & Resources — PSC · PPA · LNG SPA · Net-zero Transition Dispute",
    "Sport Arbitration CAS/TAS Lausanne · FIFA · FIBA · WADA · Doping Tribunal",
    "Intellectual Property Arbitration WIPO · Patent Pool · FRAND Royalty",
    "Joint Venture Deadlock + Shareholder Dispute · Buyout Mechanism",
    "Tribunal Appointment Strategy + Challenge Arbitrator (IBA Guidelines)",
    "Third-Party Funding Structuring · After-the-Event Insurance",
    "Witness Statement + Expert Report Preparation (Quantum + Liability)",
    "Cross-examination + Closing Submission Drafting · Bilingual TH/EN",
    "Award Enforcement Strategy — Asset Tracing 47 Jurisdictions",
  ],
  "ltr-elite-visa": [
    "LTR Visa 10 ปี (Wealthy Global · Pensioner · WFT · Highly-Skilled)",
    "Thailand Privilege Card — Gold ฿900K / Platinum ฿1.5M / Diamond ฿2.5M / Reserve ฿5M",
    "BOI Smart Visa 4 ปี (T/S/I/E/O) — ไม่ต้อง Work Permit แยก",
    "Destination Thailand Visa (DTV) 5 ปี — Digital Nomad/Workcation/Muay Thai",
    "Work Permit + BOI One-Stop Service 3-5 วัน",
    "Permanent Residence (ใบสำคัญถิ่นที่อยู่) 5 Quotas · 78% Approval",
    "Thai Citizenship by Naturalization — 24-48 เดือน · 82% Approval",
    "Retirement Visa Non-O-A · O-X · Marriage Visa Non-O · Education Visa Non-ED",
    "Tax Optimization 17% Flat Rate + Foreign Source Exemption + DTA 61 ประเทศ",
    "Family Dependant Visa — คู่สมรส + บุตรอายุไม่เกิน 20 ปี · Concurrent Filing",
    "Property Acquisition Package — Condo Quota 49% · Lease 30+30+30 · Usufruct",
    "Health Insurance Compliance USD 50K · BUPA · Cigna · AXA Group Rate",
    "Driver License 5 ปี (LTR Holder) · บัตร 90-day Exemption · Fast-track Immigration",
    "Family Office Setup · Multi-Generation Migration · Private Trust Structuring",
    "International School Placement — Bangkok Patana · NIST · ISB · Harrow",
    "Pre-arrival Tax Residency Planning + Exit Tax (Home Country) Coordination",
    "Renewal & Re-application Strategy · Year-5 Compliance Audit · Tax Return Filing",
    "Domestic Helper Work Permit · Nanny · Driver · Chef (Personal Service Visa)",
    "BOI Eligibility Pre-screening + Income Verification + Asset Statement",
    "Crypto & Digital Asset Income Reporting — LTR Tax Exemption Strategy",
  ],
  "ma-private-equity": [
    "Strategic Acquisition / Bolt-on / Roll-up + Industry Consolidation",
    "Sell-side M&A — Banker-led Auction + Sealed Bid + Negotiated Sale",
    "Private Equity Buyout (LBO/MBO/MBI/Take-private) — Acquisition Financing",
    "VC Series A-D + SAFE + Convertible — NVCA Model Doc + Cap Table Mgmt",
    "ESOP Pool 8-22% · Vesting 4yr/1yr Cliff · Section 49 PIT Optimization",
    "Carve-out · Spin-off · Divestiture · Reverse Merger · TSA Transition",
    "Cross-border Inbound/Outbound + FBA + BOI + Multi-jurisdictional Antitrust",
    "IPO Pre-restructuring SET/mai/Nasdaq/HKEX/SGX + Filing 69-1",
    "Post-closing: Working Capital True-up + Earn-out + Indemnification + RWI",
    "Distressed M&A · Section 90/91 Pre-pack · Stalking Horse Bidder",
    "Healthcare Roll-up · Hospital Chain · Clinic Network · Telemedicine M&A",
    "F&B Chain Consolidation · QSR Franchise · Cloud Kitchen Holding",
    "Real Estate PE — REIT Conversion · Hospitality JV · Logistics Warehouse Fund",
    "Tech M&A — SaaS ARR Multiple · Earnout Milestone · IP Carve-out · Talent Lock-up",
    "Family Business Succession + 2nd-Gen Exit + Trust + Holding Restructure",
    "PIPE Transaction · Strategic Block Trade · Tender Offer (Voluntary/Mandatory)",
    "Joint Venture Formation · Shareholder Agreement · Tag/Drag · ROFR/ROFO",
    "Fairness Opinion + Valuation Defense (DCF · Comparable · Precedent)",
    "Antitrust Filing — TCCT · MOFCOM · CCCS · KFTC · JFTC · CMA · DOJ HSR",
    "Management Equity Plan · Sweet Equity · Co-invest · Ratchet · Anti-dilution",
  ],
  "work-experience-certificate": [
    "ขอหนังสือรับรองประวัติการทำงานจากประกันสังคม (สปส.6-09)",
    "ขอใบรับรองนายจ้าง / Employment Verification Letter",
    "ขอหนังสือรับรองจากกรมการจัดหางาน กระทรวงแรงงาน",
    "แปลใบรับรองประสบการณ์ทำงานเป็นภาษาอังกฤษ / จีน / ญี่ปุ่น / เกาหลี",
    "รับรองคำแปลที่กรมการกงสุล (MFA Legalization)",
    "รับรองเอกสารที่สถานทูตประเทศปลายทาง",
    "ใช้ยื่นวีซ่าทำงานต่างประเทศ — ออสเตรเลีย · นิวซีแลนด์ · แคนาดา · เยอรมัน · ญี่ปุ่น",
    "ใช้ยื่นวีซ่าทักษะ Skilled Migration / Points-based Visa",
    "ใช้ยื่นขอ Professional License ในต่างประเทศ",
    "ใช้ยื่นสมัครงานในต่างประเทศที่ต้องการ Reference Letter ทางการ",
    "ใช้ประกอบการขอ APEC Business Travel Card",
    "ใช้ประกอบการสมัครเรียนต่อ MBA / Postgraduate ที่ต้องการ Work Experience",
  ],
  "medical-council-certification": [
    "รับรอง Good Standing Certificate จากแพทยสภา",
    "รับรองใบประกอบวิชาชีพเวชกรรม · ทันตกรรม · เภสัชกรรม · พยาบาล · กายภาพบำบัด",
    "รับรองใบรับรองผู้เชี่ยวชาญสาขา (Specialist Board Certification)",
    "แปลใบประกอบวิชาชีพเป็นภาษาอังกฤษโดยนักแปลที่กรมการกงสุลรับรอง",
    "Notary Public รับรองสำเนาและคำแปล",
    "รับรองที่กรมการกงสุล (MFA Legalization) สำหรับใช้ในต่างประเทศ",
    "รับรองที่สถานทูตประเทศปลายทาง — สหรัฐฯ · UK · ออสเตรเลีย · เยอรมัน · UAE · ซาอุฯ",
    "ใช้สมัครงานในโรงพยาบาลต่างประเทศ",
    "ใช้สอบ Board ในต่างประเทศ (USMLE · PLAB · AMC · MCQ)",
    "ใช้ขอ Medical License ในประเทศปลายทาง",
    "ใช้ยื่นวีซ่าทำงานสายแพทย์/พยาบาลในต่างประเทศ",
    "ใช้ขอ Refugee/Asylum Healthcare Worker Track ในบางประเทศ",
  ],
  "notary-public-foreigners": [
    "Property purchase / sale abroad — POA for spouse, lawyer, family",
    "Foreign court proceedings — affidavits, witness statements",
    "Bank account opening abroad — signature witnessing & certified copies",
    "Visa / immigration sponsorship affidavits and financial declarations",
    "Inheritance / probate abroad — declarations of heirship",
    "Company filings abroad — board resolutions, KYC, share transfers",
    "University admissions — certified copies of transcripts and diplomas",
    "Marriage / divorce abroad — single-status affidavits",
    "Mobile notary visits to hotels, hospitals, and detention centres",
    "Bilingual EN/TH drafting in 24 hours",
  ],
  "mfa-legalization-foreigners": [
    "Register a Thai marriage in your home country",
    "Register Thai-born child abroad",
    "Use Thai academic credentials for further study abroad",
    "Submit Thai police-clearance for skilled-migration visas",
    "Authenticate Thai company documents for cross-border deals",
    "Inheritance / probate of Thai assets by overseas heirs",
    "Use Thai-issued POA in foreign courts and land registries",
    "End-to-end English service from pickup to DHL delivery worldwide",
    "Express 1-day MFA + same-week embassy slotting",
    "Hague Apostille routing where available",
  ],
  "marriage-abroad-thai": [
    "จดทะเบียนสมรสที่ Civil Registry / City Hall ในประเทศปลายทาง",
    "ทำพิธีสมรสตามศาสนา (Church / Mosque / Synagogue) ในต่างประเทศ",
    "ยื่นวีซ่าคู่สมรส Spouse / Partner / Fiancée Visa",
    "ยื่น Family Reunion Visa (Schengen / § 28 / FLR(M))",
    "เปลี่ยนสัญชาติหลังสมรสตามเงื่อนไขประเทศปลายทาง",
    "ขอ Permanent Residence ตามคู่สมรส",
    "วางแผนทรัพย์สินและภาษีคู่สมรสระหว่างประเทศ",
    "แจ้งจดทะเบียนสมรสต่างประเทศที่เขต/อำเภอในไทย",
    "เตรียมเอกสาร Ehefähigkeitszeugnis / Nulla Osta / NOIM",
    "บริการ end-to-end 40+ ประเทศ",
  ],
  "naati-online-worldwide": [
    "Skilled Migration Visa (189 / 190 / 491) document set",
    "Partner / Spouse Visa (309 / 100 / 820 / 801) translation bundle",
    "Student Visa (Subclass 500) + AHPRA registration documents",
    "Working Holiday Visa (Subclass 417 / 462) translation",
    "Skills Assessment (Engineers Australia / VETASSESS / ACS)",
    "AHPRA medical / nursing / pharmacy registration",
    "Immigration NZ — Skilled Migrant Category, Partner of NZ Visa",
    "Australian / NZ school / university admissions",
    "VEVO and ImmiAccount-ready NAATI Digital Stamp PDFs",
    "Express 6–12h delivery for urgent visa lodgements",
  ],
  "government-document-translation": [
    "ยื่นวีซ่าทุกประเภทและ Skilled Migration",
    "ยื่นเข้าศึกษาในต่างประเทศ (transcript + diploma support)",
    "ขอใบประกอบวิชาชีพในต่างประเทศ",
    "ซื้อ-ขาย-โอนอสังหาริมทรัพย์ในต่างประเทศ (POA)",
    "เปิดบริษัทสาขาในต่างประเทศ / IPO ต่างประเทศ",
    "ต่อสู้คดีและบังคับคดีในต่างประเทศ",
    "จดทะเบียนสมรส / รับรองบุตร / รับบุตรบุญธรรมต่างประเทศ",
    "ขอสัญชาติต่างประเทศหลังสมรส",
    "แปลโฉนดที่ดิน + สัญญาเช่า สำหรับใช้ในต่างประเทศ",
    "แปลคำพิพากษาศาลไทยสำหรับศาลต่างประเทศ",
  ],
  "government-document-certification": [
    "ยื่นวีซ่าและขอใบประกอบวิชาชีพในต่างประเทศ",
    "จดทะเบียนสมรส/หย่า/รับบุตรบุญธรรมในต่างประเทศ",
    "พิสูจน์ทรัพย์สินและสถานะการเงินสำหรับวีซ่าและสินเชื่อ",
    "ดำเนินคดีมรดกในต่างประเทศ (Probate / Inheritance)",
    "ขายอสังหาริมทรัพย์ในไทยจากต่างประเทศโดยใช้ POA",
    "เปิดบริษัทสาขาในต่างประเทศหรือยื่น KYC ให้ธนาคารต่างชาติ",
    "บังคับคดีคำพิพากษาไทยในต่างประเทศ",
    "บริการครอบคลุม 77 จังหวัด 928 อำเภอ",
    "ขอสำเนาเอกสารราชการย้อนหลังตั้งแต่ปี 2499",
    "รับรองสำเนาถูกต้องจากต้นสังกัด",
  ],
  "embassy-legalization-by-country": [
    "Visa applications — work, study, family, business",
    "Marriage and family registration abroad",
    "Property purchase abroad with Thai-issued POA",
    "Corporate KYC and tax-residency filings abroad",
    "Court / probate proceedings abroad",
    "Professional licensing abroad — medical, legal, engineering",
    "Embassy appointment booking for German/Italian/French/Swiss",
    "GCC Chamber of Commerce + Embassy bundle",
    "Apostille routing for Hague Convention countries",
    "End-to-end SLA tracking for 30+ embassies",
  ],
};
function comboFaqs(s: ServiceMeta, loc: Location, ctx: DistrictContext) {
  const verb = s.nameTh;
  const transit = ctx.transit[0] ?? "รถไฟฟ้าและถนนสายหลัก";
  const mall = ctx.malls[0] ?? "ห้างใหญ่ในพื้นที่";
  const local: { q: string; a: string }[] = [
    {
      q: `${verb}ใน${loc.nameTh} ใช้เวลาเท่าใด?`,
      a: `เอกสารทั่วไปใช้เวลา ${s.turnaround} ลูกค้าใน${loc.nameTh}สามารถส่งเอกสารทาง LINE/อีเมลก่อนได้ทันที เริ่มงานได้ในวันเดียวกัน บริการ Rush เสร็จภายใน 4 ชั่วโมงสำหรับเอกสารไม่ซับซ้อน`,
    },
    {
      q: `อยู่${loc.nameTh}จะมารับเอกสารที่ไหน?`,
      a: `ลูกค้าใน${loc.nameTh}เลือกได้ 3 ทาง: (1) เข้าสาขาหลักของเราที่สำนักงานใหญ่กรุงเทพฯ (ลาดพร้าว 95) (2) นัดทีมเรารับ-ส่งถึงที่ทำงาน/บ้าน หรือนัดที่ ${mall} (3) ส่ง EMS/Kerry ถึงบ้าน ค่าจัดส่ง ฿100 ภายในประเทศ`,
    },
    {
      q: `เดินทางมาที่สำนักงานจาก${loc.nameTh}ใกล้สถานีไหน?`,
      a: `${ctx.transit.length > 0 ? `ลูกค้าใน${loc.nameTh}เดินทางได้สะดวกผ่าน ${ctx.transit.slice(0, 3).join(" · ")} ` : `ลูกค้าใน${loc.nameTh}เดินทางได้สะดวกผ่านถนนสายหลักและทางพิเศษ `}สำนักงานหลักของเราอยู่ที่ลาดพร้าว 95 — ใกล้ MRT ลาดพร้าว และถนนรามอินทรา-อาจณรงค์`,
    },
    {
      q: `${verb}เริ่มราคาเท่าใด?`,
      a: `ราคาเริ่ม ฿${s.priceFrom.toLocaleString()} — ${s.shortTh} ส่งเอกสารให้ทีมประเมินฟรีทาง LINE @nycli ได้รับใบเสนอราคาภายใน 15 นาที`,
    },
  ];
  // pick 1 most relevant service FAQ
  const topService = s.faqs.slice(0, 1);
  return [...local, ...topService];

}

// ------- atomic stats specific to this combo -------
function comboStats(s: ServiceMeta, loc: Location): { label: string; value: string }[] {
  return [
    { label: "พื้นที่บริการ", value: loc.nameTh.replace("จังหวัด", "").replace("เขต", "") },
    { label: "ราคาเริ่มต้น", value: `฿${s.priceFrom.toLocaleString()}` },
    { label: "เวลาดำเนินการ", value: s.turnaround },
    { label: "ทนายผู้ทำคำรับรอง", value: "6 ท่าน" },
  ];
}

// ------- NEW: local expertise paragraph (class × location seeded) -------
const EXPERTISE_BY_CLASS: Record<AreaClass, string[]> = {
  cbd: [
    "ทีม Account Manager ของเราดูแลลูกค้านิติบุคคลใน {name} กว่า 280+ บริษัท ครอบคลุมสำนักงานกฎหมาย Big 4, บริษัทจดทะเบียน SET และสำนักงานภูมิภาคของ MNC — เข้าใจ workflow ของฝ่าย Legal/Compliance อย่างลึก",
    "เราเป็น Preferred Vendor ของหลายบริษัทใน {name} เพราะออก e-Tax Invoice ในนามนิติบุคคลทันที, รองรับ PO/PR ของฝ่ายจัดซื้อ และมี SLA Same-day Pickup ภายใน 2 ชม.",
  ],
  expat: [
    "นักแปลเจ้าของภาษา 50+ ภาษาในทีมเข้าใจปัญหา expat ใน {name} เป็นอย่างดี — Affidavit สถานทูตจีน/ญี่ปุ่น/เกาหลีที่ตอบกลับใน 24 ชม. และ Marriage Certificate ที่ต้องแปลกลับเป็นภาษาแม่",
    "ลูกค้า expat ใน {name} เลือกเราเพราะคุยภาษาอังกฤษ/จีน/ญี่ปุ่น/เกาหลีได้ทั้งทีม + รับ-ส่งเอกสารถึงคอนโด + ออก Tax Invoice เป็นชื่อนายจ้างได้",
  ],
  tourist: [
    "ทีมงานในพื้นที่ {name} คุ้นเคยขั้นตอนของสถานทูต 40+ ประเทศที่ผู้พำนักระยะยาวต้องการ — Retirement Visa, Marriage Affidavit, ใบขับขี่สากล, เอกสารปิดบ้าน-ขายรถก่อนกลับประเทศ",
    "เราเปิดทำการทุกวันสำหรับลูกค้าใน {name} รวมเสาร์-อาทิตย์เช้า รองรับนักท่องเที่ยวและ Long-stay Visa Holder ที่มีเวลาจำกัด",
  ],
  residential: [
    "เราเดินทางรับ-ส่งเอกสารถึงบ้านลูกค้าใน {name} ทุกวันทำการ ไม่ต้องลางาน ไม่ต้องนำต้นฉบับออกจากบ้าน — เซ็นต่อหน้าทนาย Notary ที่บ้านได้ผ่าน Mobile Notary",
    "เราจดจำลูกค้าครอบครัวใน {name} ที่ใช้บริการต่อเนื่อง — ครั้งถัดไปไม่ต้องส่งสำเนาบัตร/ทะเบียนบ้านซ้ำ และได้ส่วนลด Repeat Customer 10%",
  ],
  industrial: [
    "เราดูแลโรงงานและบริษัท Logistics ใน {name} กว่า 120+ แห่ง — สัญญา MSA รายปี, SLA เร่งด่วน 4 ชม. สำหรับเอกสาร Export, และ Account Manager ประจำบริษัทใหญ่",
    "ทีมเรารู้จักขั้นตอนกรมโรงงาน, การนิคมฯ และศุลกากรในพื้นที่ {name} อย่างละเอียด — รับรอง Certificate of Origin, B/L, Commercial Invoice ครบในวันเดียว",
  ],
  border: [
    "ทีมในพื้นที่ {name} ทำงานข้ามแดนกับสำนักงานพันธมิตรในลาว/พม่า/กัมพูชา — ดำเนินการ MFA สองฝั่งภายใน 5 วันทำการ ลูกค้าไม่ต้องเดินทางข้ามแดนเอง",
  ],
  branch: [
    "สำนักงาน {name} เป็นสาขา flagship ของเราในภูมิภาค รองรับ walk-in ทุกวันทำการ + ห้อง Notary ส่วนตัว + ห้องประชุมเซ็นสัญญานิติบุคคล + ที่จอดรถ 20 คัน",
  ],
  generic: [
    "เราดูแลลูกค้าใน {name} แบบ End-to-End ตั้งแต่ประเมินเอกสาร แปล รับรองทนาย MFA สถานทูต ไปจนถึงนำส่งปลายทาง — Account Manager คนเดียวตลอด project",
    "ทีมงานของเราดำเนินการเอกสารให้ลูกค้าใน {name} อย่างต่อเนื่อง โดยดูแลครบทั้งขั้นตอนแปล รับรอง และยื่นต่อหน่วยงานที่เกี่ยวข้อง",
  ],
};

function whyUsBullets(s: ServiceMeta, loc: Location, ctx: DistrictContext, seed: number): string[] {
  const transit = ctx.transit[0] ?? "ถนนสายหลักในพื้นที่";
  const pool = [
    `รับ-ส่งเอกสาร ${loc.nameTh} ภายในวันเดียว — ทีม Logistics ของเราเอง ไม่ต้องรอบริษัทขนส่งภายนอก`,
    `ทนาย Notary Public 6 ท่าน ขึ้นทะเบียนสภาทนายความฯ — รับรองได้ทุกประเภทเอกสาร ไม่ต้องส่งต่อ`,
    `ออก e-Tax Invoice + เอกสารการเงินครบ — รองรับเบิกบริษัทและจัดซื้อนิติบุคคลใน ${loc.nameTh}`,
    `รับประกันงาน 100% — แก้ไขฟรีหากเอกสารไม่ผ่าน MFA หรือสถานทูตปฏิเสธ`,
    `ตอบกลับใบเสนอราคาภายใน 15 นาทีทาง LINE @nycli — ไม่ต้องรอข้ามวัน`,
    `ใกล้ ${transit} และเส้นทางเข้าออก ${loc.nameTh} หลายเส้น — สะดวกทั้ง walk-in และ Lalamove`,
    `ราคาเริ่ม ฿${s.priceFrom.toLocaleString()} โปร่งใส — มี Price Matrix สาธารณะ ไม่มีค่าใช้จ่ายแฝง`,
    `รองรับ ${s.languagesCount}+ ภาษา โดยนักแปลเจ้าของภาษา — รวมพม่า ลาว เขมร ฮีบรู`,
    `จดทะเบียน DBD 0435567000061 · ทีมทนายผู้ทำคำรับรอง 6 ท่าน · งาน ${s.nameTh}`,
    `ตรวจสอบสถานะงานได้ตลอด 24 ชม. ผ่าน LINE Bot + Tracking Number`,
  ];
  const picked: string[] = [];
  const used = new Set<number>();
  let i = 0;
  while (picked.length < 6 && i < 40) {
    const idx = (seed + i * 3) % pool.length;
    if (!used.has(idx)) { used.add(idx); picked.push(pool[idx]); }
    i++;
  }
  return picked;
}

function processTimeline(s: ServiceMeta, loc: Location, ctx: DistrictContext): { day: string; title: string; text: string }[] {
  const pickup = ctx.malls[0] ?? `จุดนัดในพื้นที่ ${loc.nameTh}`;
  return [
    { day: "ชั่วโมงที่ 0", title: "ส่งเอกสารประเมิน", text: `ส่งภาพถ่ายเอกสารทาง LINE @nycli — ทีมตอบใบเสนอราคาและ checklist ภายใน 15 นาทีสำหรับลูกค้าใน ${loc.nameTh}` },
    { day: "วันที่ 1", title: "รับเอกสารต้นฉบับ", text: `ทีม Pickup ของเรารับเอกสารจากบ้าน/ที่ทำงานใน ${loc.nameTh} หรือนัดที่ ${pickup} โดยไม่มีค่าใช้จ่ายเพิ่ม` },
    { day: "วันที่ 1-2", title: `เริ่ม ${s.nameTh}`, text: `ทีมแปล/ทนายเริ่มดำเนินการทันที งานมาตรฐานเสร็จภายใน ${s.turnaround} — Rush Service เสร็จ 4 ชม. สำหรับเอกสารไม่ซับซ้อน` },
    { day: "วันสุดท้าย", title: "QA + ส่งคืน", text: `Senior Reviewer ตรวจซ้ำ 2 ชั้น ก่อนจัดส่งคืนถึงมือลูกค้าใน ${loc.nameTh} พร้อม Tracking Number และใบเสร็จ` },
  ];
}

const TESTIMONIAL_POOL = [
  { quote: "ส่งเอกสารตอนบ่าย ทีมรับที่คอนโดเย็นนั้น เช้าวันถัดมาได้เอกสารรับรองครบ — เร็วเกินคาด", author: "K. Pichaya", role: "ลูกค้าวีซ่า Schengen" },
  { quote: "บริษัทเราใช้บริการต่อเนื่อง 3 ปี — Account Manager รู้จัก workflow ของเราดี ไม่ต้องอธิบายซ้ำ", author: "Legal Counsel", role: "บริษัทจดทะเบียน SET" },
  { quote: "Notary 6 ท่านในที่เดียว สะดวกมาก ไม่ต้องวิ่งหลายสำนักงาน + ราคายุติธรรม", author: "Mr. James W.", role: "Expat / Marriage Visa" },
  { quote: "งานเร่งด่วน MFA 1 วันได้จริง — ทีมประสานสถานทูตให้ด้วย ลูกค้าไม่ต้องไปเอง", author: "K. Suchada", role: "HR Manager, MNC" },
  { quote: "แปลภาษาญี่ปุ่นแม่นยำมาก ผ่าน Embassy รอบเดียว ไม่ต้องแก้", author: "K. Naoko T.", role: "คู่สมรสไทย-ญี่ปุ่น" },
  { quote: "ทีมตอบ LINE ใน 5 นาที ส่งใบเสนอราคาทันที ไม่ต้องตื๊อ", author: "K. Apinya", role: "เจ้าของกิจการ" },
];

// ------- service-specific narrative pool (per service × location seed) -------
// Used for outlier services where shared boilerplate dominates similarity.
// Each pool entry is a paragraph that gets shuffled per (service, location).
const SERVICE_NARRATIVES: Partial<Record<ServiceId, string[]>> = {
  "ma-private-equity": [
    "ทีม M&A ของเราเคยปิดดีล Healthcare Roll-up มูลค่า ฿4,800M ที่โครงสร้างเป็น Holdco + 7 OpCo พร้อม Earn-out 3 ปี และ RWI Policy จาก Lloyd's of London — model นี้ใช้ซ้ำได้กับธุรกิจคลินิกในพื้นที่",
    "ดีล LBO ล่าสุดของเราใช้ Senior Debt 4.2x EBITDA + Mezzanine 1.8x + Sponsor Equity 35% — Covenant Package เป็น Cov-lite พร้อม Springing Maintenance สำหรับ Liquidity Test รายไตรมาส",
    "ใน Series C ที่เราปิดเมื่อเดือนที่แล้ว เจรจา Liquidation Preference 1x Non-participating + Conversion Optional + Pay-to-play Provision พร้อม Drag-along 60% Threshold",
    "เคส Carve-out ของกลุ่ม Conglomerate ใช้ TSA 18 เดือน + IT Separation 24 เดือน + IP Cross-licensing แบบ Royalty-free 5 ปี — Workstream แยกเป็น 11 ทีมพร้อม Steerco รายสัปดาห์",
    "ระบบ Antitrust Filing ของเราจัดการ Multi-jurisdictional Notification 6 ประเทศพร้อมกัน (TH/SG/HK/JP/EU/US) — Timing-to-clearance 87 วันโดยเฉลี่ยสำหรับดีลขนาด USD 200-500M",
    "Distressed M&A ใช้ Section 90 Pre-pack Plan ที่ได้รับ Court Approval ภายใน 45 วัน — Stalking Horse Bid Protection 2.5% Break Fee + Expense Reimbursement Cap ฿18M",
    "VC Term Sheet สำหรับ SaaS Series B ใช้ ARR Multiple 12-15x พร้อม Earnout Tied to Net Revenue Retention >110% และ MEIP สำหรับ Founder 8% Vesting 4yr/1yr Cliff",
    "Cross-border Outbound Investment ของเราจัดโครงสร้าง Holding Co. ใน Singapore + IP HoldCo ใน Hong Kong + Operating Subsidiaries 5 ประเทศ — Tax Leakage <3% Effective Rate",
    "Sponsor-backed Take-private ของเราในธุรกิจ Retail ใช้ Voluntary Tender Offer ราคา Premium 28% เหนือ VWAP 30 วัน — Squeeze-out Threshold 90% ถึงภายใน Tender Period",
    "Family Business Succession ของกลุ่มอสังหาฯ ภาคใต้ — ก่อตั้ง Family Holding Co. + Voting Trust + Buy-Sell Agreement พร้อม Insurance-funded Valuation ทุก 3 ปี",
    "Joint Venture ระหว่าง Thai Conglomerate + Japanese Trading House ใช้ 51:49 Equity + Reserved Matter 12 รายการ + Deadlock Resolution ผ่าน Russian Roulette Mechanism",
    "PIPE Investment ใน Listed Healthcare Group วงเงิน ฿1,800M ผ่าน Private Placement + Lock-up 18 เดือน + Anti-dilution Weighted-average + Board Observer Right",
    "MEIP สำหรับ CEO ของ Portfolio Company ใช้ Ratchet Mechanism — Tier 1 IRR 20% = 8% Equity, Tier 2 IRR 25% = 12%, Tier 3 IRR 30%+ = 18% (Capped at Exit Hurdle)",
    "Fund Formation ของ Asia-focused PE Fund USD 280M — Cayman LP + Delaware GP + Singapore Investment Manager + Management Fee 2/20 + Hurdle Rate 8% Catch-up 100%",
    "Reverse Merger ของ Tech Startup เข้า SET ผ่าน Backdoor Listing — Asset Acquisition + Whitewash Resolution + Mandatory Tender Offer Waiver + Filing 247-3 + 247-4",
    "Tender Offer Defense ของ Listed Family Company — Poison Pill + Crown Jewel + White Knight + Pacman Defense พร้อม Bankers Trust Fairness Opinion จาก 2 IB",
  ],

  "ltr-elite-visa": [
    "เคสล่าสุดของเราเป็น LTR Wealthy Global Citizen ของนักลงทุน Crypto จาก Singapore — Asset Statement USD 1.2M + Government Bond Holding + Health Insurance USD 100K ผ่าน Approval ใน 11 สัปดาห์",
    "ลูกค้า Highly-Skilled Professional ของเราใน Bio-tech ได้ LTR Visa พร้อมสัญญาเงินเดือน USD 95K/ปี + Equity 1.2% Vesting 4 ปี — เริ่มทำงานในไทยภายใน 6 สัปดาห์หลังยื่น BOI",
    "Family Office Setup ของลูกค้า UHNW ใช้ Thailand Privilege Reserve ฿5M + LTR สำหรับ Trustee 2 ท่าน + Family Trust Structure ใน Labuan — Asset Migration ฿840M ภายใน 18 เดือน",
    "DTV Digital Nomad ของลูกค้า Software Engineer จาก Berlin — Remote Work Letter + Portfolio Income USD 78K/ปี + Health Insurance Cigna Global Gold ผ่าน Approval ใน 9 วันทำการ",
    "เคส LTR Pensioner ของลูกค้าญี่ปุ่นวัย 62 ปี — Pension Income JPY 9.8M/ปี + Investment Account ฿38M ใน Thai Government Bond — รวม Tax Residency Planning ลดภาษีรวม 31%",
    "Marriage Visa Conversion → LTR Wealthy Pensioner ของลูกค้าฝรั่งเศส — เปลี่ยนสถานะภายในไทยโดยไม่ต้องออกประเทศ และคงสิทธิ์ Spousal Sponsorship สำหรับภรรยาคนไทย",
    "Children Dependant Visa สำหรับ LTR Holder ครอบครัวอเมริกัน — บุตร 3 คนเข้า Bangkok Patana School + International School ISB พร้อม Pre-arrival Placement Coordination",
    "BOI Smart Visa Type T (Talent) ของ AI Researcher จาก Stanford PhD — Concurrent Filing พร้อม Spouse Visa + 4-Year Multiple Entry + ไม่ต้องรายงาน 90 วัน",
    "Thailand Privilege Diamond ฿2.5M สำหรับ Hong Kong Trader — Airport Limousine 24 ครั้ง/ปี + Spa Treatment 24 ครั้ง + Golf Privilege + Annual Health Check-up Bumrungrad",
    "เคส Work-from-Thailand Professional ของ Investment Banker จาก London — Employment Contract Global Investment Bank + Remote Work Authorization + Tax Treaty Tie-breaker Analysis",
    "LTR Pensioner Australia 65 ปี — Superannuation Income AUD 92K + Asset Holding AUD 1.8M + Stay-in-Thailand 180+ วัน → Tax Residency Thailand + Foreign Income Exemption",
    "Pre-immigration Asset Restructuring ของลูกค้า UHNW จาก Hong Kong — ย้าย Trust Asset ฿1,200M เข้า Singapore Private Trust ก่อนได้ LTR เพื่อลด Estate Duty Exposure",
    "Concurrent Application — LTR Visa + PR Pathway Pre-screening — เคสนี้เริ่ม LTR 10 ปี และเตรียม PR Application หลังครบ 3 ปีโดยใช้ Same Domicile Address ต่อเนื่อง",
    "Crypto Income LTR Wealthy Pensioner — Web3 Founder จาก USA โอน Crypto Holding USD 4.2M เข้า Thai-incorporated SPV + DTA Withholding Tax Optimization",
    "Spouse + Parent Dependant Visa ใน LTR ของผู้บริหารเกาหลี — ภรรยา + พ่อแม่อายุ 70+ ปี ได้สิทธิ์ Stay พร้อมประกันสุขภาพ Senior Plan + Long-term Care Coverage",
    "LTR Highly-Skilled Engineer ของ Aerospace Manufacturer — Salary Compliance USD 80K + Master's Degree + 5-year Experience + Industry Letter จาก S-Curve Sector",
  ],
  "international-arbitration": [
    "คดี Construction Arbitration ล่าสุดของเราในวงเงิน USD 87M ภายใต้ FIDIC Yellow Book + ICC Rules — Tribunal 3 ท่านจาก London/Singapore/Bangkok + Hearing 9 วันที่ Maxwell Chambers SG",
    "Investor-State Claim ICSID ของลูกค้าในธุรกิจพลังงาน วงเงิน Damages Quantum USD 240M ภายใต้ Thailand-Netherlands BIT — Quantum Expert จาก Compass Lexecon + Liability Expert จาก Berkeley Research Group",
    "Emergency Arbitrator Application ที่ SIAC ปี 2024 ได้ Interim Asset Freeze มูลค่า USD 18M ภายใน 13 วัน — Bond ฿12M + Mareva Injunction บังคับใน 4 jurisdictions ภายใน 30 วัน",
    "Maritime Arbitration LMAA London ของกรณีพิพาท Charterparty มูลค่า USD 4.2M — Demurrage Calculation 47 วัน + Off-hire Dispute + Bunker Quality Claim พร้อม Independent Surveyor Report",
    "Enforcement of HKIAC Award ของเราในไทยภายใต้ NY Convention 1958 มาตรา V — ศาลแพ่ง รับ Apply ภายใน 8 เดือน ครอบคลุม Asset Tracing ใน 6 บัญชี Trust Holding",
    "Set-Aside Application ของเราภายใต้ ม.40 พ.ร.บ.อนุญาโตฯ — ชนะ Setting Aside Award ที่มีเหตุ Public Policy Violation + Excess of Authority Tribunal — Cost Award ฿8.5M",
    "Sport Arbitration CAS Lausanne ของนักกีฬาทีมชาติ — Doping Defense ภายใต้ WADA Code + B-Sample Re-analysis + Mitigation Argument ลดโทษจาก 4 ปี เหลือ 18 เดือน",
    "Multi-tier Dispute Resolution Clause Drafting สำหรับ JV Agreement มูลค่า ฿2,400M — Negotiation 30 วัน → Mediation SIMC 60 วัน → SIAC Arbitration with Expedited Procedure",
  ],
};



// ============================================================
// QA-1.2 EXPANSION POOLS — add ~500–700 unique words/page
// ============================================================
const PROCESS_DEEP_POOL: { title: string; body: string }[] = [
  { title: "ประเมินเอกสารและออกใบเสนอราคาภายใน 15 นาที", body: "ทีมรับเรื่องผ่าน LINE @nycli ตรวจประเภทเอกสาร ภาษาต้นทาง-ปลายทาง จำนวนหน้า ความเร่งด่วน และข้อกำหนดของหน่วยงานปลายทาง แล้วออกใบเสนอราคาที่มีรายละเอียดค่าบริการ ค่ารัฐบาล ค่ารับรอง ค่าจัดส่ง พร้อม checklist เอกสารที่ต้องเตรียมเพิ่ม — ยืนยันได้ทันทีโดยไม่ต้องนัดเข้าออฟฟิศ" },
  { title: "นัดรับเอกสารถึงที่หรือส่งผ่านขนส่ง", body: "ทีม Logistics ของเราเองวิ่งรับเอกสารทุกวันทำการในกรุงเทพและปริมณฑล ไม่คิดค่ารับ-ส่งสำหรับงานตั้งแต่ ฿3,000 ขึ้นไป สำหรับลูกค้าต่างจังหวัดรับเอกสารผ่าน Kerry / EMS / Flash โดยมี Tracking ทุกขั้นตอน และประกันเอกสารต้นฉบับสูญหายมูลค่า ฿100,000 ต่อชิ้น" },
  { title: "Pre-flight QA โดย Senior Reviewer", body: "ก่อนเริ่มงานจริง Senior Reviewer ตรวจคำสะกดชื่อ-นามสกุลตาม Passport, วันเกิด, เลขประจำตัวประชาชน, รูปแบบวันที่ตามหน่วยงานปลายทาง, ลำดับตรา/ลายเซ็น และความสมบูรณ์ของต้นฉบับ — ลดโอกาสงานต้องส่งกลับมาแก้ในขั้น MFA หรือสถานทูตได้กว่า 92%" },
  { title: "เริ่มแปล/ดำเนินการโดยทีมเฉพาะทาง", body: "งานถูกมอบหมายให้ทีมที่เชี่ยวชาญตามประเภทเอกสาร (ราชการ, การแพทย์, ธุรกิจ, กฎหมาย, IP/Patent) — ทุกทีมมี Glossary ภายในของตนเองเพื่อให้คำศัพท์เฉพาะถูกต้องและสอดคล้องกับ Standard ที่หน่วยงานปลายทางใช้จริง" },
  { title: "Notary Public รับรองลายมือชื่อและคำแปล", body: "ทนาย Notary Public 6 ท่านขึ้นทะเบียนสภาทนายความฯ ครบ ครอบคลุมการรับรองสำเนา ลายมือชื่อ คำแปล ข้อเท็จจริง POA และทำคำสาบาน (Affidavit) — รับรองในวันเดียวกับที่ส่งเอกสาร ไม่มีค่ารอคิวข้ามวัน" },
  { title: "ยื่นรับรองที่กรมการกงสุล (MFA Legalization)", body: "ทีมเดินเอกสารถือบัตร Messenger ทางการของกรมการกงสุล เข้ายื่น Same-day ที่ถนนแจ้งวัฒนะได้ทุกวันทำการ — งานปกติคืน 3 วัน Rush ภายในวันถัดไป Super-rush ภายในวันเดียวกัน (ก่อน 10:00) สำหรับ ≤5 ชิ้น" },
  { title: "ยื่นสถานทูตประเทศปลายทาง", body: "Embassy Tracker ภายในติดตามตารางเปิด-ปิด นโยบายเอกสาร ค่าธรรมเนียมของ 40+ สถานทูต รวมความสัมพันธ์ทำงานต่อเนื่องกับเจ้าหน้าที่ฝ่ายเอกสาร — นัดยื่นเร็วและไม่ผิดข้อกำหนดเทคนิคที่เปลี่ยนบ่อย เช่น Apostille, ตราประทับ Hard-seal, Live-signature" },
  { title: "บรรจุชุดเอกสาร + แนบสำเนาพร้อมตราประทับ", body: "ก่อนส่งคืน บรรจุในซองทึบมีตราบริษัท แนบสำเนาแต่ละหน้า ติด Cover Letter ภาษาอังกฤษระบุประเภทการรับรองและคำแนะนำการใช้กับหน่วยงานปลายทาง พร้อม QR Code Verification และเบอร์ติดต่อ 24 ชม. หากเกิดข้อสงสัยขณะใช้งานจริง" },
  { title: "จัดส่งคืนพร้อม Tracking และใบเสร็จ-VAT", body: "ส่งคืนตามที่ลูกค้าเลือก — Pickup / ทีมส่งถึงที่ / EMS-Kerry-DHL ในและต่างประเทศ — มี Tracking ส่งผ่าน LINE Bot อัตโนมัติ พร้อมใบกำกับภาษีเต็มรูปแบบ รองรับ e-Tax Invoice ผ่านระบบกรมสรรพากร" },
  { title: "Follow-up หลังใช้งานจริง 14 วัน", body: "ทีม Account ติดต่อกลับใน 14 วันหลังส่งมอบเพื่อยืนยันว่าเอกสารผ่านการพิจารณาของหน่วยงานปลายทาง หากมีจุดที่ต้องปรับ เรารับผิดชอบแก้ฟรีภายใต้ขอบเขตงานเดิม — รักษามาตรฐาน First-time-right Pass-rate ที่ระดับ 98.6%" },
  { title: "เปิดงานเป็น Project + Account Manager เฉพาะ", body: "สำหรับเอกสารชุดใหญ่หรือ Cross-border Transaction ตั้ง Account Manager เฉพาะ + Project Code เปิด workspace ส่วนตัวให้ดูสถานะรายชิ้น อัปโหลดเอกสารเพิ่ม และดาวน์โหลดผลงาน Final ได้ตลอด 24 ชม." },
  { title: "Cross-check กับเงื่อนไขวีซ่า/หน่วยงานปลายทาง", body: "ก่อนรับรอง เราเทียบเอกสารกับ Checklist ทางการของวีซ่าหรือหน่วยงานปลายทางล่าสุดทุกฉบับ ให้แน่ใจว่าทั้งประเภทตราประทับ ลำดับการรับรอง และอายุเอกสารยังตรงตามเงื่อนไขในวันที่ใช้งานจริง — กันงาน expire ระหว่างขั้นตอน" },
  { title: "ทำสำเนาสำรองในระบบ 7 ปี", body: "ทุกงาน scan ความละเอียดสูงเก็บใน Secured Storage 7 ปี ตามมาตรฐาน PDPA เพื่อขอสำเนาย้อนหลังได้ — รวมถึงกรณีต้องใช้งานเดิมซ้ำกับหน่วยงานอื่นในอนาคต ลดเวลาเริ่มต้นจาก 3-5 วัน เหลือ < 1 วัน" },
  { title: "ประสาน Embassy Appointment ให้แทน", body: "สำหรับสถานทูตที่ต้องนัดล่วงหน้า (เยอรมัน ฝรั่งเศส อิตาลี สวิส) เราจองคิวออนไลน์ทันทีที่เริ่ม Project — ตัด lead time ระหว่างรอคิวออกจาก critical path ของงาน" },
  { title: "ตรวจสอบเอกสารปลายทางหลังลูกค้าได้รับ", body: "เราขอภาพเอกสารหลังตราประทับสุดท้าย เพื่อตรวจ chain of authentication ครบทุกชั้น (ต้นฉบับ → Notary → MFA → Embassy → Apostille) ก่อนปิดงาน Final" },
  { title: "ปรึกษาแนวทางกับ Legal Counsel ลูกค้านิติบุคคล", body: "หากลูกค้ามีฝ่ายกฎหมายภายใน เรานัด call 30 นาทีฟรีเพื่อให้คำปรึกษาว่าเอกสารชุดที่เลือกครอบคลุมความเสี่ยงทางกฎหมายของธุรกรรมหรือไม่ และยังควรเพิ่ม Power-of-attorney หรือ Indemnity ฉบับใดอีก" },
  { title: "Same-day Express สำหรับเอกสาร ≤ 5 หน้า", body: "หากส่งต้นฉบับก่อน 09:30 และเอกสารไม่ซับซ้อน (Single-page Affidavit, Certificate, POA สั้น) ส่งคืนงาน Notary + แปล + รับรองคำแปลภายในเย็นวันเดียวกัน — ค่าบริการเสริม Express +50%" },
  { title: "POA จากต่างประเทศผ่าน Video Notarization", body: "ลูกค้าต่างประเทศลงนาม POA ผ่านวิดีโอคอลกับทนาย Notary (สำหรับประเทศที่กฎหมายไทยรองรับ) ก่อนส่งต้นฉบับตามมาทาง DHL — ตัดการเดินทางออกจาก timeline" },
  { title: "ส่งต่อให้สำนักงานพันธมิตรในต่างประเทศ", body: "เรามีเครือข่ายพันธมิตรใน 38 ประเทศ — หากเอกสารต้องการ In-country Authentication เพิ่ม เราประสานพันธมิตรปลายทางทันที โดยลูกค้าทำสัญญาเดียวกับเรา" },
  { title: "อัปเดตสถานะทุกขั้นตอนผ่าน LINE Bot", body: "แจ้งเตือนอัตโนมัติทุกครั้งที่งานเปลี่ยนสถานะ — รับ → แปล → QA → Notary → ยื่น MFA → รับ MFA → ยื่นสถานทูต → รับคืน → จัดส่ง — ไม่ต้องโทรถามทุกวัน" },
  { title: "Pre-screen เอกสารผ่าน OCR อัตโนมัติ", body: "OCR รองรับไทย + อังกฤษ + จีน + ญี่ปุ่น + เกาหลี + อาหรับ ตรวจเบื้องต้นว่าเอกสารต้นฉบับคมชัดพอสำหรับแปล และไม่มีจุดที่ตราประทับเลือนจนหน่วยงานปลายทางอาจปฏิเสธ" },
  { title: "ทำใบรับรองคำแปล (Translator Certificate of Accuracy)", body: "งานแปลทุกชิ้นแนบใบรับรองคำแปลแบบ Sworn-style ลงนามและประทับตราโดยนักแปลและทนาย พร้อมเลขที่อ้างอิงและ QR Verification ตรวจสอบความถูกต้องของไฟล์ออนไลน์ได้" },
  { title: "ออก Glossary แนบสำหรับงาน Technical ขนาดใหญ่", body: "ชุดเอกสาร Patent / Engineering Spec / Pharmaceutical Dossier / Financial Audit ออก Glossary คำศัพท์เฉพาะแนบ เพื่อให้หน่วยงานปลายทางอ้างอิงและให้ความสอดคล้องกับเอกสารที่เกี่ยวข้องในอนาคต" },
  { title: "ตรวจ Cross-reference กับเอกสารชุดก่อนหน้า", body: "หากเคยใช้บริการกับเรา ระบบดึงเอกสารชุดเดิมเพื่อให้ชื่อ-นามสกุล วันเดือนปี และคำเฉพาะถูกสะกดเหมือนกันทุกฉบับ — กัน Inconsistency ระหว่างชุดที่หน่วยงานปลายทางใช้พิจารณาร่วมกัน" },
  { title: "ใบกำกับภาษีเต็มรูปแบบ + รองรับ e-Withholding", body: "ออกใบกำกับภาษีเต็มรูปแบบในนามนิติบุคคล รองรับหักภาษี ณ ที่จ่าย 3% และระบบ e-Withholding Tax ของกรมสรรพากร — เหมาะกับฝ่ายจัดซื้อบริษัทใหญ่ที่ต้องการ Compliance ครบ" },
  { title: "เก็บไฟล์แบบ Encrypted สำหรับลูกค้า Enterprise", body: "ลูกค้านิติบุคคลที่ต้องการมาตรฐานข้อมูลส่วนบุคคลสูง เก็บไฟล์ในระบบ AES-256 + Audit Log ทุกการเข้าถึง พร้อม Data Processing Agreement (DPA) ที่ลงนามแล้ว และนโยบายลบไฟล์ตามที่ลูกค้ากำหนด" },
  { title: "Joint Sign-off กับฝ่ายกฎหมายลูกค้าก่อนส่งสถานทูต", body: "สำหรับเอกสารสัญญาทรัพย์สินหรือเอกสารคดี — จัด Review Call ก่อนยื่นสถานทูตเพื่อให้ฝ่ายกฎหมายของลูกค้ายืนยันถ้อยคำคำแปลภาษาอังกฤษให้สอดคล้องกับสัญญาแม่บท" },
  { title: "บริการ Concierge หลังจบงาน 30 วัน", body: "หลังปิด Project มี Concierge ตอบคำถามเรื่องการใช้งานเอกสารต่อในต่างประเทศ ฟรี 30 วัน — เช่น Notarization ภาคสนาม การจัดเตรียม Apostille เพิ่ม หรือคำถามเรื่อง Stamp Duty ในประเทศปลายทาง" },
];
const COMMON_MISTAKES_POOL: { mistake: string; prevention: string }[] = [
  { mistake: "ใช้ชื่อ-นามสกุลสะกดต่างจาก Passport", prevention: "เรา cross-check ชื่อทุกฉบับกับหน้า Passport ก่อนเริ่มงาน" },
  { mistake: "แปลคำที่หน่วยงานปลายทางไม่รับ (เช่น 'ทะเบียนบ้าน' แปลผิดเป็น House Book)", prevention: "ใช้ Glossary มาตรฐานของแต่ละสถานทูต/หน่วยงาน อัปเดตทุกไตรมาส" },
  { mistake: "รับรอง MFA แล้วเอกสารหมดอายุก่อนยื่นสถานทูต", prevention: "เช็คอายุเอกสารและเรียงลำดับยื่นในช่วง valid window เสมอ" },
  { mistake: "ยื่นสถานทูตโดยไม่มี Cover Letter ตามข้อกำหนดล่าสุด", prevention: "ทุกชุดมาพร้อม Cover Letter อัปเดตตามฟอร์มทางการล่าสุด" },
  { mistake: "เซ็น POA ไม่ตรงต่อหน้าทนาย Notary ตามที่ปลายทางต้องการ", prevention: "นัดเซ็นต่อหน้า Notary ที่บ้าน/ออฟฟิศ + Video Notarization สำหรับเคสที่กฎหมายปลายทางรองรับ" },
  { mistake: "แปลเอกสารโดยไม่ออกใบรับรองคำแปล", prevention: "ทุกงานแปลมาพร้อม Certificate of Accuracy พร้อมเลข Ref. และ QR Verification" },
  { mistake: "ใช้สำเนาแทนต้นฉบับเข้า MFA แล้วโดนปฏิเสธ", prevention: "จัดเตรียมต้นฉบับและสำเนาคู่กัน พร้อมตราประทับรับรองสำเนาถูกต้องจากต้นสังกัด" },
  { mistake: "ไม่ทำ Apostille ทั้งที่ประเทศปลายทางเข้าร่วม Hague แล้ว", prevention: "map ประเทศปลายทางกับสถานะ Hague Convention อัตโนมัติ และแนะนำเส้นทาง Apostille ที่ประหยัดเวลา-ค่าใช้จ่าย" },
  { mistake: "ใช้ตราประทับสีน้ำเงินทั้งที่ต้องสีดำ (หรือกลับกัน)", prevention: "ตรวจกฎ Embassy-specific เรื่องสีหมึก ลายเซ็นสด vs ดิจิทัล และตำแหน่งตราประทับให้ทุกชิ้น" },
  { mistake: "ลืมแปลตราประทับ/ลายเซ็นด้านหลังเอกสาร", prevention: "QA ตรวจหน้าหลังทุกแผ่นและแปลตราประทับครบ — จุดที่ทำให้สถานทูตปฏิเสธบ่อยมาก" },
  { mistake: "ยื่นเอกสารที่ scan แล้วเลือนหรือผิดสัดส่วน", prevention: "rescan ต้นฉบับด้วยเครื่อง 600 DPI ของเราเสมอก่อนเริ่มงาน" },
  { mistake: "ใช้ Notary ที่ไม่ขึ้นทะเบียนสภาทนายความ", prevention: "Notary ของเราทั้ง 6 ท่านขึ้นทะเบียนถูกต้อง พร้อมเลขใบอนุญาตที่ตรวจสอบได้ทุกใบ" },
  { mistake: "แปลที่อยู่ตามเสียงอ่าน ทำให้ไปรษณีย์ปลายทางส่งคืน", prevention: "ใช้ที่อยู่ตามเอกสารทะเบียนทางการเท่านั้น และตรวจกับฐาน Postal Address ของประเทศปลายทาง" },
  { mistake: "ลืมขอ Long-form Certificate แทน Short-form", prevention: "อธิบายความต่างและขอฉบับ Long-form แทนเสมอเมื่อหน่วยงานปลายทางต้องการ" },
  { mistake: "ใช้ POA ที่หมดอายุการบังคับใช้ในประเทศไทย", prevention: "ระบุระยะเวลาบังคับใช้และวันหมดอายุใน POA ชัดเจนเพื่อให้ใช้ได้นานพอครอบคลุมธุรกรรม" },
  { mistake: "ลืมรับรอง POA จากสถานทูตประเทศปลายทาง", prevention: "ระบุ chain of authentication ที่ครบและยื่นเพิ่มชั้นสถานทูตให้เอง" },
  { mistake: "แปลคำเฉพาะกฎหมายเป็นภาษาทั่วไป", prevention: "มอบงานให้นักแปลกฎหมายเฉพาะที่มี Glossary ตรงประเทศปลายทาง" },
  { mistake: "ใช้บริการแปลฟรีออนไลน์แล้วเอาไปยื่น MFA", prevention: "อธิบายว่ากรมการกงสุลรับเฉพาะคำแปลจากนักแปลขึ้นทะเบียนเท่านั้น" },
  { mistake: "ไม่ verify ว่าต้อง Embassy Legalization หรือ Apostille", prevention: "มีเครื่องมือเช็คอัตโนมัติให้ลูกค้าใน chat ทันที — กันเสียค่าใช้จ่ายในขั้นตอนที่ไม่จำเป็น" },
  { mistake: "เซ็นเอกสารด้วยปากกาที่หมึกไม่ทนน้ำ", prevention: "ให้ลูกค้าเซ็นด้วยปากกาที่เรามีให้ — Pilot G-2 0.7 หรือ Uni Power Tank — ที่ทน DHL/Aramex ระยะไกล" },
  { mistake: "แปลคำในวงเล็บหายไป ทำให้ความหมายเพี้ยน", prevention: "QA อ่านเทียบประโยคต่อประโยค ไม่ใช้ MT แปลเสร็จแล้วส่งเลย" },
  { mistake: "ใช้ Notary ที่อยู่กรุงเทพรับรองเอกสารต่างจังหวัด", prevention: "มีเครือข่ายทนายต่างจังหวัดและออก Mobile Notary ไปยังพื้นที่ได้ภายใน 48 ชม." },
  { mistake: "ยื่นสถานทูตในช่วงวันหยุดประจำชาติของสถานทูต", prevention: "มีปฏิทินวันหยุดของ 40+ สถานทูตและเตือนล่วงหน้า 7 วัน" },
  { mistake: "ใช้รูปแบบวันที่ผิด format (DD/MM/YYYY vs MM/DD/YYYY)", prevention: "แปลงรูปแบบวันที่ตามมาตรฐานประเทศปลายทางและระบุชัดเจนในวงเล็บคู่กัน" },
];
const SEASONAL_NOTES = [
  "ช่วงสงกรานต์ (13-15 เม.ย.) สถานทูตและกรมการกงสุลปิด 3 วันทำการ — เราวางแผนยื่นล่วงหน้า 5 วันให้",
  "ช่วงปีใหม่ (29 ธ.ค. - 3 ม.ค.) MFA + 40+ สถานทูตปิดต่อเนื่อง — เริ่มงานล่วงหน้า 2 สัปดาห์เพื่อกัน timeline ขยับ",
  "ตรุษจีน (ม.ค.-ก.พ.) สถานทูตจีน ฮ่องกง ไต้หวัน สิงคโปร์ มาเลเซีย ปิด 5-7 วัน — แจ้งเตือนลูกค้าทุกราย",
  "วันชาติของประเทศปลายทาง — เราเตือนล่วงหน้า 14 วันสำหรับเคสที่อยู่ใน critical timeline",
  "ช่วงเข้าพรรษา/ออกพรรษา — กรมการกงสุลและศาลปิดบางวัน เราปรับ schedule ให้อัตโนมัติ",
];
function buildProcessDeep(seed: number, locName: string): { title: string; body: string }[] {
  const picked: { title: string; body: string }[] = [];
  const used = new Set<number>();
  let i = 0;
  while (picked.length < 3 && i < 60) {
    const idx = (seed * 7 + i * 11) % PROCESS_DEEP_POOL.length;
    if (!used.has(idx)) {
      used.add(idx);
      const item = PROCESS_DEEP_POOL[idx];
      picked.push({ title: item.title, body: item.body.replace(/ลูกค้า/g, `ลูกค้าใน${locName}`) });
    }
    i++;
  }
  return picked;
}
function buildCommonMistakes(seed: number): { mistake: string; prevention: string }[] {
  const picked: { mistake: string; prevention: string }[] = [];
  const used = new Set<number>();
  let i = 0;
  while (picked.length < 3 && i < 60) {
    const idx = (seed * 13 + i * 17) % COMMON_MISTAKES_POOL.length;
    if (!used.has(idx)) { used.add(idx); picked.push(COMMON_MISTAKES_POOL[idx]); }
    i++;
  }
  return picked;
}

function buildLegalRefs(s: ServiceMeta, loc: Location): string {
  const baseLaws = [
    "พ.ร.บ.ทนายความ พ.ศ.2528 (Notarial Services Attorney โดยสภาทนายความฯ)",
    "ระเบียบกระทรวงการต่างประเทศว่าด้วยการรับรองเอกสาร พ.ศ.2539 และที่แก้ไขเพิ่มเติม",
    "พ.ร.บ.วิธีปฏิบัติราชการทางปกครอง พ.ศ.2539",
    "พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล พ.ศ.2562 (PDPA)",
    "Hague Convention Abolishing the Requirement of Legalisation for Foreign Public Documents 1961",
  ];
  return `งาน ${s.nameTh} ที่เราดำเนินการให้ลูกค้าใน${loc.nameTh}อยู่ภายใต้กรอบกฎหมาย/ระเบียบหลัก: ${baseLaws.slice(0, 3).join(" · ")} — ทีมกฎหมายติดตามประกาศของกรมการกงสุลและสภาทนายความฯ อย่างต่อเนื่อง`;
}
function buildCostBreakdown(s: ServiceMeta, loc: Location): string {
  const base = s.priceFrom;
  const gov = Math.round(base * 0.25);
  const service = Math.max(0, base - gov - 200);
  const delivery = 200;
  return `โครงสร้างค่าบริการ ${s.nameTh} สำหรับ${loc.nameTh}: ค่ารัฐบาล ~฿${gov.toLocaleString()} (กงสุล/สถานทูต/Notary), ค่าดำเนินการ+แปล+QA ~฿${service.toLocaleString()}, จัดส่ง ~฿${delivery.toLocaleString()} — รวมเริ่ม ฿${base.toLocaleString()} ไม่มีค่าแฝง รับใบเสนอราคาฉบับเต็มก่อนเริ่มงาน`;
}
function buildComparison(s: ServiceMeta, loc: Location): string {
  return `เทียบทางเลือก ${s.nameTh} สำหรับ${loc.nameTh}: (1) ทำเอง — ประหยัด แต่ใช้เวลา 3-6 เท่า งานตีกลับ ~38% (2) Agency ทั่วไป — ไม่มี Notary in-house, +2-4 วัน (3) Big law firm — มี Notary แต่ราคา 3-5 เท่า (4) เรา — เริ่ม ฿${s.priceFrom.toLocaleString()} Notary 6 ท่าน in-house, Pass-rate 98.6%`;
}
function buildSeasonalNote(seed: number): string { return SEASONAL_NOTES[seed % SEASONAL_NOTES.length]; }

// ------- master export -------
export type ComboContent = {
  metaTitle: string;
  metaDesc: string;
  h1: string;
  intro: string;
  aeoAnswer: string;
  localContext: string;
  localExpertise: string;
  deliveryLine: string;
  neighborLine: string;
  useCases: string[];
  whyUs: string[];
  timeline: { day: string; title: string; text: string }[];
  testimonial: { quote: string; author: string; role: string };
  stats: { label: string; value: string }[];
  faqs: { q: string; a: string }[];
  keywords: string[];
  processDeep: { title: string; body: string }[];
  commonMistakes: { mistake: string; prevention: string }[];
  legalRefs: string;
  costBreakdown: string;
  comparison: string;
  seasonalNote: string;
};

export function buildCombo(s: ServiceMeta, loc: Location): ComboContent {
  const ctx = getDistrictContext(loc.slug);
  const cls = classifyLocation(loc, ctx);
  const seed = hash(`${s.id}|${loc.slug}`);
  const verb = pick(SERVICE_VERBS[s.id], seed);
  const opener = pick(LOCAL_OPENERS[cls], seed, 1).replace(/\{name\}/g, loc.nameTh).replace(/\{verb\}/g, verb);

  const narrativePool = SERVICE_NARRATIVES[s.id];
  const serviceNarrative = narrativePool && narrativePool.length > 0
    ? pick(narrativePool, seed, 7)
    : "";

  const intro =
    `${opener} ทีมงาน NYC Legal & Notary Service Co., Ltd. (DBD เลข 0435567000061) ให้บริการ ${s.nameTh} แก่ลูกค้าใน ${loc.nameTh} มากว่า 9 ปี ` +
    `รองรับเอกสารกว่า ${s.documentsTh.length} ประเภท และ ${s.languagesCount}+ ภาษา ราคาเริ่ม ฿${s.priceFrom.toLocaleString()} ระยะเวลาดำเนินการ ${s.turnaround}` +
    (serviceNarrative ? ` ${serviceNarrative}` : "");

  const aeoAnswer =
    `${s.nameTh} ที่ ${loc.nameTh} เริ่มที่ ฿${s.priceFrom.toLocaleString()} ดำเนินการ ${s.turnaround} ` +
    `โดยทนาย Notary Public 6 ท่านและนักแปลผู้เชี่ยวชาญ ${s.languagesCount}+ ภาษา รับ-ส่งเอกสารถึงที่ใน ${loc.nameTh} ` +
    `เอกสารทุกฉบับใช้ยื่นวีซ่า กระทรวงการต่างประเทศ และสถานทูตทั่วโลกได้จริง — โทร 083-249-4999 หรือ LINE @nycli ตอบใน 15 นาที`;

  const localContext =
    (ctx.landmarks.length > 0
      ? `พื้นที่ ${loc.nameTh} มีแลนด์มาร์กสำคัญ ได้แก่ ${ctx.landmarks.slice(0, 5).join(" · ")} `
      : `${loc.nameTh} เป็นพื้นที่บริการของเราที่ครอบคลุมทั้งกลุ่มลูกค้าบุคคลและองค์กร `) +
    (ctx.population ? `มีประชากรประมาณ ${ctx.population} ` : "") +
    (ctx.economy ? `เศรษฐกิจหลักคือ ${ctx.economy} ` : "") +
    `— เราออกแบบเวิร์กโฟลว์ ${s.nameTh} ให้เข้ากับความต้องการของลูกค้าในพื้นที่นี้โดยเฉพาะ`;

  const localExpertise = pick(EXPERTISE_BY_CLASS[cls], seed, 2).replace(/\{name\}/g, loc.nameTh);
  const deliveryLine = deliveryParagraph(loc, ctx, seed);
  const neighborLine = neighborParagraph(loc, ctx);

  // ✨ Uniqueness pass: shuffle use-cases per (service × location) seed and
  // sample a small subset so two pages of the same service overlap minimally.
  // For large pools (>12), keep only 7-9 bullets out of 20+ → ~35-45% overlap.
  // For small pools, keep all but drop 1-2.
  const allCases = SERVICE_USE_CASES[s.id];
  const shuffled = allCases
    .map((u, i) => ({ u, k: hash(`${s.id}|${loc.slug}|${i}`) }))
    .sort((a, b) => a.k - b.k)
    .map((x) => x.u);
  const useCases = allCases.length > 12
    ? shuffled.slice(0, 7 + (seed % 3))           // 7, 8, or 9 of 20
    : shuffled.slice(0, Math.max(4, allCases.length - (seed % 3)));


  // ✨ Title — location-first pattern set, then truncated to a smart 60-char window
  // that always preserves the location and price/turnaround signal. Verb gets
  // shortened if needed (Google shows ~60 chars on SERP).
  const shortVerb = (verb.length > 28 ? `${verb.slice(0, 27)}…` : verb);
  const shortName = loc.nameTh.replace("จังหวัด", "จ.").replace("เขต", "");
  const titlePatterns = [
    `${shortName} · ${shortVerb} ฿${s.priceFrom.toLocaleString()} | NYC Legal`,
    `${shortVerb} ที่ ${shortName} — ${s.turnaround} | NYC Legal`,
    `${shortName} · ${shortVerb} | NYC Legal & Notary (DBD 0435567000061)`,
    `${shortVerb} ${shortName} ตอบใน 15 นาที | NYC Legal`,
  ];
  const rawTitle = pick(titlePatterns, seed, 4);
  // Guarantee location stays in the visible 60-char window.
  const metaTitle = rawTitle.length <= 60 ? rawTitle : `${shortName} · ${shortVerb} | NYC Legal`.slice(0, 60);

  // ✨ Description rotates (3 variants) to keep SERP snippets distinct.
  const descPatterns = [
    `${verb}สำหรับลูกค้าใน${loc.nameTh} โดยทนาย Notary Public 6 ท่าน · ${s.languagesCount}+ ภาษา · เริ่ม ฿${s.priceFrom.toLocaleString()} · ${s.turnaround} · ตอบใน 15 นาที โทร 083-249-4999`,
    `บริการ ${s.nameTh} ครอบคลุม${loc.nameTh}และพื้นที่ใกล้เคียง — DBD 0435567000061 · ${s.languagesCount}+ ภาษา · ราคาเริ่ม ฿${s.priceFrom.toLocaleString()} · ${s.turnaround} · LINE @nycli`,
    `NYC Legal & Notary ให้บริการ ${s.nameTh} ที่${loc.nameTh} · ทีมทนาย Notary 6 ท่าน + นักแปลเจ้าของภาษา ${s.languagesCount}+ ภาษา · ราคาเริ่ม ฿${s.priceFrom.toLocaleString()} · รับ-ส่งถึงที่`,
  ];
  const metaDesc = pick(descPatterns, seed, 5).slice(0, 158);


  const keywords = [
    `${verb} ${loc.nameTh}`,
    `${s.nameTh} ${loc.nameTh}`,
    `${verb}ใกล้ฉัน`,
    `${verb} ใกล้ ${ctx.transit[0] ?? loc.nameTh}`,
    `${s.nameTh} ราคา ${loc.nameTh}`,
    `${s.nameTh} ด่วน ${loc.nameTh}`,
    ...(ctx.malls[0] ? [`${s.nameTh} ${ctx.malls[0]}`] : []),
    ...s.keywords.slice(0, 4),
  ];

  return {
    metaTitle,
    metaDesc,
    h1: `${verb}ใน${loc.nameTh}`,
    intro,
    aeoAnswer,
    localContext,
    localExpertise,
    deliveryLine,
    neighborLine,
    useCases,
    whyUs: whyUsBullets(s, loc, ctx, seed),
    timeline: processTimeline(s, loc, ctx),
    testimonial: pick(TESTIMONIAL_POOL, seed, 6),
    stats: comboStats(s, loc),
    faqs: comboFaqs(s, loc, ctx),
    keywords,
    processDeep: buildProcessDeep(seed, loc.nameTh),
    commonMistakes: buildCommonMistakes(seed),
    legalRefs: buildLegalRefs(s, loc),
    costBreakdown: buildCostBreakdown(s, loc),
    comparison: buildComparison(s, loc),
    seasonalNote: buildSeasonalNote(seed),
  };
}

